import React, { useState, useEffect, useLayoutEffect, useRef, useCallback, useMemo } from "react";
import { axiosInstance } from "@/App";
import { toast } from "sonner";
import { 
  Video, 
  Users, 
  Share2,
  X,
  ArrowLeft,
  Settings,
  Shield,
  MessageCircle,
  Send,
  ExternalLink,
  Volume2,
  Coins,
  Lock,
  Flag,
  LogOut
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import {
  LiveKitRoom,
  GridLayout,
  ParticipantTile,
  RoomAudioRenderer,
  useTracks,
  useLiveKitRoom,
  useParticipants,
  useRoomContext,
  VideoTrack,
} from "@livekit/components-react";
import "@livekit/components-styles";
import { Track, RoomEvent, VideoQuality } from "livekit-client";
import { useScreenProtection } from "@/hooks/useScreenProtection";
import ScreenProtectionOverlay, { ProtectedContent } from "@/components/ScreenProtectionOverlay";
import { useReactionProgression, BASE_REACTION_EMOJIS, getReactionType } from "@/hooks/useReactionProgression";
import LiveReactionLayer from "@/components/LiveReactionLayer";
import ShowMeDonationDollarLayer, {
  getShowMeDonationDollarCount,
  normalizeShowMeDonationDollarCount,
} from "@/components/ShowMeDonationDollarLayer";
import { getProModeTransitionDuration } from "@/utils/proModeTransitions";
import StreamDiagnostics from "@/components/StreamDiagnostics";
import { shouldForceViewerRoomReconnect } from "@/utils/viewerRecovery";
import IvsPlaybackLayer from "@/components/IvsPlaybackLayer";
import DirectRemoteVideo from "@/components/DirectRemoteVideo";
import {
  areReactionsAudienceVisible,
  getEventExperience,
  getReactionProfileForCategory,
  isVisibleEventCategory,
} from "@/config/eventCategories";
import {
  getTithePresetById,
  TITHE_PRESETS,
} from "@/config/viewerPaymentOptions";
import { getDonationEffectSpec } from "@/config/donationEffects";
import {
  CREATOR_CAMERA_BACKGROUND_FIT,
  getCameraDisplayFit,
} from "@/utils/cameraCapture";
import { STREAM_ORIENTATION } from "@/utils/eventCreation";
import { getProViewerSubscription } from "@/utils/proModeMedia";
import { mergeChatMessages } from "@/utils/chatMessages";
import {
  CHAT_SOCKET_HEARTBEAT_MS,
  advanceChatRecoveryCursor,
  getAccessStatusPollDelay,
  getChatRecoveryParams,
  getSpeakerStatusPollDelay,
  getViewerMediaHandoffPolicy,
  isAccessRevocationSocketClose,
} from "@/utils/liveControlPlane";
import {
  getAudienceAudioCaptureOptions,
  getAudienceAudioPublishOptions,
} from "@/utils/broadcastQuality";
import {
  getMediaRecoveryDelay,
  isMediaTrackHealthy,
} from "@/utils/mobileStreamRecovery";
import CreatorSettingsPortal from "@/components/CreatorSettingsPortal";
import { LIVE_REPORT_REASONS } from "@/config/liveReportReasons";

import {
  LiveChatLane,
  LiveShareSheet,
  LiveTopBar,
  ViewerInteractionDock,
  useLiveViewportLock,
} from "@/components/LiveExperienceChrome";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;
const PRO_MODE_COMMAND_TOPIC = "showme-pro-control";
const STRIPE_JS_URL = "https://js.stripe.com/clover/stripe.js";
let stripeJsPromise;

const loadStripeJs = () => {
  if (window.Stripe) return Promise.resolve(window.Stripe);
  if (stripeJsPromise) return stripeJsPromise;
  stripeJsPromise = new Promise((resolve, reject) => {
    const existing = document.querySelector(`script[src="${STRIPE_JS_URL}"]`);
    const script = existing || document.createElement("script");
    const handleLoad = () => window.Stripe
      ? resolve(window.Stripe)
      : reject(new Error("Stripe.js did not initialize"));
    const handleError = () => reject(new Error("Stripe.js could not be loaded"));
    script.addEventListener("load", handleLoad, { once: true });
    script.addEventListener("error", handleError, { once: true });
    if (!existing) {
      script.src = STRIPE_JS_URL;
      script.async = true;
      document.head.appendChild(script);
    }
  });
  return stripeJsPromise;
};

const proModeViewerTransitionStyles = `
  .viewer-program-feed {
    position: absolute;
    inset: 0;
    backface-visibility: hidden;
    transform: translateZ(0);
    will-change: opacity, transform;
  }
  .viewer-program-feed-incoming { opacity: 0; }
  @keyframes viewerProgramFadeOut {
    0% { opacity: 1; }
    48%, 100% { opacity: 0; }
  }
  @keyframes viewerProgramFadeIn {
    0%, 52% { opacity: 0; }
    100% { opacity: 1; }
  }
  @keyframes viewerProgramDissolveOut {
    from { opacity: 1; }
    to { opacity: 0; }
  }
  @keyframes viewerProgramDissolveIn {
    from { opacity: 0; }
    to { opacity: 1; }
  }
  @keyframes viewerProgramBlendOut {
    from { opacity: 1; transform: scale(1); }
    to { opacity: 0; transform: scale(.97); }
  }
  @keyframes viewerProgramBlendIn {
    from { opacity: 0; transform: scale(1.04); }
    to { opacity: 1; transform: scale(1); }
  }
  .viewer-transition-fade .viewer-program-feed-outgoing { animation: viewerProgramFadeOut 560ms cubic-bezier(.45,0,.55,1) forwards; }
  .viewer-transition-fade .viewer-program-feed-incoming { animation: viewerProgramFadeIn 560ms cubic-bezier(.45,0,.55,1) forwards; }
  .viewer-transition-dissolve .viewer-program-feed-outgoing { animation: viewerProgramDissolveOut 680ms linear forwards; }
  .viewer-transition-dissolve .viewer-program-feed-incoming { animation: viewerProgramDissolveIn 680ms linear forwards; }
  .viewer-transition-blend .viewer-program-feed-outgoing { animation: viewerProgramBlendOut 760ms cubic-bezier(.4,0,.2,1) forwards; }
  .viewer-transition-blend .viewer-program-feed-incoming { animation: viewerProgramBlendIn 760ms cubic-bezier(.4,0,.2,1) forwards; }
  .viewer-transition-cut .viewer-program-feed-outgoing { opacity: 0; }
  .viewer-transition-cut .viewer-program-feed-incoming { opacity: 1; }
`;

const VIEWER_DEVICE_PROFILES = {
  tv: { id: "tv", label: "Smart TV", width: 1920, height: 1080 },
  laptop: { id: "laptop", label: "Laptop", width: 1680, height: 1050 },
  "phone-large": { id: "phone-large", label: "Phone - large", width: 1290, height: 2796 },
  "phone-small": { id: "phone-small", label: "Phone - small", width: 1080, height: 2340 },
};

const VIEWER_DEVICE_ALIASES = {
  tv: "tv",
  "smart-tv": "tv",
  smarttv: "tv",
  laptop: "laptop",
  mobl: "phone-large",
  "phone-large": "phone-large",
  phonelarge: "phone-large",
  mobs: "phone-small",
  "phone-small": "phone-small",
  phonesmall: "phone-small",
};

export const resolveViewerDeviceProfile = (viewerDevice, eventOrientation = "portrait") => {
  const rawId = typeof viewerDevice === "string" ? viewerDevice : viewerDevice?.id || viewerDevice?.key;
  const normalizedId = String(rawId || "").trim().toLowerCase().replace(/[_\s]/g, "-");
  const alias = VIEWER_DEVICE_ALIASES[normalizedId] || VIEWER_DEVICE_ALIASES[normalizedId.replace(/-/g, "")];
  if (alias) return VIEWER_DEVICE_PROFILES[alias];
  return eventOrientation === "landscape"
    ? { id: "event-landscape", label: "Landscape", width: 1920, height: 1080 }
    : { id: "event-portrait", label: "Portrait", width: 1080, height: 1920 };
};

// True only for phones/tablets — never for desktop or smart-TV/console browsers
// (so the rotate prompt is shown on mobile only, per requirements 1–3).
const detectIsMobile = () => {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent || "";
  if (/(smart-?tv|googletv|appletv|crkey|roku|web0s|tizen|hbbtv|netcast|viera|bravia|playstation|xbox)/i.test(ua)) return false;
  const uaMobile = /Mobi|Android|iPhone|iPad|iPod|IEMobile|BlackBerry|Opera Mini/i.test(ua);
  const coarse = typeof window !== "undefined" && window.matchMedia && window.matchMedia("(pointer: coarse)").matches;
  const smallish = typeof window !== "undefined" && Math.min(window.screen?.width || 9999, window.screen?.height || 9999) <= 1024;
  return uaMobile || (coarse && smallish);
};

// Mandatory pre-roll prompt on mobile when the device orientation doesn't match
// the event's. Rendered INSTEAD of the player, so no video plays behind it.
// Continue unlocks once the device matches the target orientation (or via a
// fallback for devices that can't rotate/report).
const RotationPrompt = ({ isLandscape, orientation, onContinue, onBack }) => {
  const targetLandscape = orientation === "landscape";
  const [allowFallback, setAllowFallback] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => setAllowFallback(true), 6000); // devices that can't rotate/report
    return () => clearTimeout(t);
  }, []);
  const matches = targetLandscape ? isLandscape : !isLandscape;
  const canContinue = matches || allowFallback;

  return (
    <div className="fixed inset-0 z-[60] bg-black flex flex-col items-center justify-center px-6 text-center" data-testid="rotate-prompt">
      <style>{`
        @keyframes smlPhoneRotate {
          0%,15%   { transform: rotate(0deg); }
          45%,70%  { transform: rotate(-90deg); }
          100%     { transform: rotate(-90deg); }
        }
        @keyframes smlPhoneUpright {
          0%,15%   { transform: rotate(-90deg); }
          45%,70%  { transform: rotate(0deg); }
          100%     { transform: rotate(0deg); }
        }
        .sml-phone { animation: ${targetLandscape ? "smlPhoneRotate" : "smlPhoneUpright"} 2.6s cubic-bezier(.6,.05,.3,1) infinite; }
      `}</style>

      <button onClick={onBack} className="absolute top-4 left-4 text-white/70 hover:text-white p-2 rounded-full bg-white/10" data-testid="rotate-back">
        <ArrowLeft className="w-5 h-5" />
      </button>

      <div className="mb-8 h-28 flex items-center justify-center">
        <div className="sml-phone w-16 h-28 rounded-[12px] border-[3px] border-white/90 flex items-center justify-center">
          <div className="w-9 h-20 rounded bg-white/25" />
          <div className="absolute w-1.5 h-1.5 rounded-full bg-white/60" style={{ transform: "translateY(42px)" }} />
        </div>
      </div>

      <h2 className="text-white text-2xl font-black mb-2">{targetLandscape ? "Rotate Your Device" : "Hold Your Phone Upright"}</h2>
      <p className="text-gray-300 max-w-xs mb-1">
        This event is streaming in <span className="text-white font-semibold">{targetLandscape ? "Landscape Mode" : "Portrait Mode"}</span>. {targetLandscape ? "Turn your mobile device sideways" : "Keep your phone vertical"} for the best viewing experience.
      </p>
      <p className="text-blue-400 text-sm mb-8">{targetLandscape ? "Landscape" : "Portrait"} Mode provides the best viewing experience.</p>

      <button
        onClick={onContinue}
        disabled={!canContinue}
        className={`px-8 py-3 rounded-full font-bold transition-all ${
          canContinue ? "bg-blue-600 hover:bg-blue-700 text-white" : "bg-white/10 text-white/40 cursor-not-allowed"
        }`}
        data-testid="rotate-continue"
      >
        {canContinue ? "Continue" : (targetLandscape ? "Rotate to continue…" : "Hold upright to continue…")}
      </button>
    </div>
  );
};

// Audio enabler for mobile browsers (handles autoplay restrictions)
const AudioEnabler = () => {
  const room = useRoomContext();
  const [needsEnable, setNeedsEnable] = useState(false);

  useEffect(() => {
    if (!room) return undefined;
    const check = () => {
      if (!room.canPlaybackAudio) setNeedsEnable(true);
      else setNeedsEnable(false);
    };
    const resumeAfterInterruption = async () => {
      if (document.visibilityState === "hidden") return;
      try {
        await room.startAudio();
      } catch {
        // iOS can require another user gesture after a phone call. In that
        // case the existing Enable Audio button remains visible as fallback.
      } finally {
        check();
      }
    };
    const onVisibility = () => {
      if (document.visibilityState === "visible") resumeAfterInterruption();
    };
    check();
    room.on(RoomEvent.AudioPlaybackStatusChanged, check);
    room.on(RoomEvent.Reconnected, resumeAfterInterruption);
    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("focus", resumeAfterInterruption);
    window.addEventListener("pageshow", resumeAfterInterruption);
    return () => {
      room.off(RoomEvent.AudioPlaybackStatusChanged, check);
      room.off(RoomEvent.Reconnected, resumeAfterInterruption);
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("focus", resumeAfterInterruption);
      window.removeEventListener("pageshow", resumeAfterInterruption);
    };
  }, [room]);

  if (!needsEnable) return null;

  return (
    <button
      onClick={async () => {
        try {
          await room.startAudio();
          setNeedsEnable(false);
        } catch (e) { console.error("startAudio failed", e); }
      }}
      className="absolute top-14 left-1/2 -translate-x-1/2 z-50 bg-blue-600 hover:bg-blue-700 text-white px-5 py-2.5 rounded-full font-medium text-sm flex items-center gap-2 shadow-lg animate-pulse"
      data-testid="enable-audio-btn"
    >
      <Volume2 className="w-4 h-4" />
      Tap to Enable Audio
    </button>
  );
};

// Religion audiences participate through ShowMeCongregation and reactions,
// never through an open microphone. Viewer tokens are already subscribe-only;
// this guard also keeps the browser-side local microphone muted if a stale or
// third-party track ever appears in the room.
const ReligionViewerMicrophoneGuard = ({ enabled }) => {
  const room = useRoomContext();

  useEffect(() => {
    if (!enabled || !room?.localParticipant) return undefined;
    let disposed = false;

    const enforceMutedMicrophone = async () => {
      if (disposed) return;
      const publication = room.localParticipant.getTrackPublication(Track.Source.Microphone);
      if (!publication || publication.isMuted) return;
      try {
        await room.localParticipant.setMicrophoneEnabled(false);
      } catch (muteError) {
        console.warn('[ReligionViewer] unable to remute unexpected local microphone track:', muteError);
      }
    };

    enforceMutedMicrophone();
    room.on(RoomEvent.LocalTrackPublished, enforceMutedMicrophone);
    const watchdog = window.setInterval(enforceMutedMicrophone, 1000);
    return () => {
      disposed = true;
      window.clearInterval(watchdog);
      room.off(RoomEvent.LocalTrackPublished, enforceMutedMicrophone);
    };
  }, [enabled, room]);

  return null;
};

// Education and Podcast viewers stay subscribe-only until the creator accepts
// their raise-hand or Call-In request. This controller never touches video and only publishes the
// local microphone after the viewer taps the mic button (required by mobile
// browser permission policies).
const EducationViewerMicrophoneController = ({
  enabled,
  controlRef,
  speakerStatus,
  microphoneEnabled = false,
}) => {
  const room = useRoomContext();
  const desiredMicrophoneEnabledRef = useRef(false);
  const recoveryTimerRef = useRef(null);
  const recoveryInProgressRef = useRef(false);

  useEffect(() => {
    if (!enabled || !room?.localParticipant || !controlRef) return undefined;
    let disposed = false;

    const setEnabled = async (nextEnabled, { force = false } = {}) => {
      if (disposed) return false;
      desiredMicrophoneEnabledRef.current = nextEnabled;
      if (!nextEnabled) {
        await room.localParticipant.setMicrophoneEnabled(false).catch(() => {});
        return false;
      }

      // LiveKit delivers server-side permission changes asynchronously. A
      // brief bounded retry avoids making the viewer tap twice without ever
      // reconnecting or reacquiring the creator camera.
      let lastError;
      for (let attempt = 0; attempt < 4; attempt += 1) {
        try {
          const existing = room.localParticipant.getTrackPublication(Track.Source.Microphone);
          if (force && existing) {
            await room.localParticipant.setMicrophoneEnabled(false).catch(() => {});
          }
          await room.localParticipant.setMicrophoneEnabled(
            true,
            getAudienceAudioCaptureOptions(),
            getAudienceAudioPublishOptions(),
          );
          const publication = room.localParticipant.getTrackPublication(Track.Source.Microphone);
          const mediaTrack = (publication?.audioTrack || publication?.track)?.mediaStreamTrack;
          if (mediaTrack && "contentHint" in mediaTrack) mediaTrack.contentHint = "speech";
          if (isMediaTrackHealthy(mediaTrack, publication?.isMuted)) return true;
          throw new Error("The microphone track did not become active");
        } catch (error) {
          lastError = error;
          force = true;
          await new Promise((resolve) => window.setTimeout(resolve, 250 * (attempt + 1)));
        }
      }
      throw lastError || new Error("Microphone permission is not ready");
    };

    const recoverMicrophone = async () => {
      if (
        disposed
        || !desiredMicrophoneEnabledRef.current
        || document.visibilityState === "hidden"
        || recoveryInProgressRef.current
      ) return;
      const publication = room.localParticipant.getTrackPublication(Track.Source.Microphone);
      const mediaTrack = (publication?.audioTrack || publication?.track)?.mediaStreamTrack;
      if (isMediaTrackHealthy(mediaTrack, publication?.isMuted)) return;

      recoveryInProgressRef.current = true;
      try {
        await setEnabled(true, { force: Boolean(publication) });
      } catch (error) {
        console.warn("[Audience microphone] automatic recovery is waiting for the device:", error);
      } finally {
        recoveryInProgressRef.current = false;
      }
    };

    const scheduleRecovery = (reason = "focus") => {
      if (!desiredMicrophoneEnabledRef.current || disposed) return;
      window.clearTimeout(recoveryTimerRef.current);
      recoveryTimerRef.current = window.setTimeout(recoverMicrophone, getMediaRecoveryDelay(reason));
    };
    const onVisibility = () => {
      if (document.visibilityState === "visible") scheduleRecovery("visibility");
    };

    controlRef.current = { setEnabled, scheduleRecovery };
    room.on(RoomEvent.Reconnected, scheduleRecovery);
    room.on(RoomEvent.LocalTrackPublished, scheduleRecovery);
    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("focus", scheduleRecovery);
    window.addEventListener("pageshow", scheduleRecovery);
    window.addEventListener("online", scheduleRecovery);
    const watchdog = window.setInterval(recoverMicrophone, 2500);
    return () => {
      disposed = true;
      if (controlRef.current?.setEnabled === setEnabled) controlRef.current = null;
      window.clearInterval(watchdog);
      window.clearTimeout(recoveryTimerRef.current);
      room.off(RoomEvent.Reconnected, scheduleRecovery);
      room.off(RoomEvent.LocalTrackPublished, scheduleRecovery);
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("focus", scheduleRecovery);
      window.removeEventListener("pageshow", scheduleRecovery);
      window.removeEventListener("online", scheduleRecovery);
      room.localParticipant.setMicrophoneEnabled(false).catch(() => {});
    };
  }, [controlRef, enabled, room]);

  useEffect(() => {
    if (!enabled || !room?.localParticipant) return;
    const shouldPublish = speakerStatus === "speaking" && microphoneEnabled;
    desiredMicrophoneEnabledRef.current = shouldPublish;
    if (!shouldPublish) {
      controlRef.current?.setEnabled(false).catch(() => {});
    } else {
      controlRef.current?.scheduleRecovery("status");
    }
  }, [controlRef, enabled, microphoneEnabled, room, speakerStatus]);

  return null;
};

// A selected audience speaker stays visually on AWS IVS, but needs the
// creator's low-latency room audio to hold a real conversation. Subscribe to
// creator audio only; never subscribe to a LiveKit video track from this
// hidden bridge. RoomAudioRenderer attaches only those selected audio tracks.
const AudienceSpeakerProgramAudio = ({ enabled }) => {
  const room = useRoomContext();

  useEffect(() => {
    if (!room) return undefined;

    const syncCreatorAudioSubscriptions = () => {
      room.remoteParticipants?.forEach?.((participant) => {
        const identity = String(participant.identity || "");
        const isCreatorProgram = identity.startsWith("creator_") || /^Camera-\d+$/.test(identity);
        participant.trackPublications?.forEach?.((publication) => {
          const isAudio = publication.kind === Track.Kind.Audio
            || publication.source === Track.Source.Microphone
            || publication.source === Track.Source.ScreenShareAudio;
          if (!isAudio) return;
          const shouldSubscribe = Boolean(enabled && isCreatorProgram);
          if (publication.isSubscribed !== shouldSubscribe) {
            publication.setSubscribed?.(shouldSubscribe);
          }
        });
      });
    };

    syncCreatorAudioSubscriptions();
    room.on(RoomEvent.ParticipantConnected, syncCreatorAudioSubscriptions);
    room.on(RoomEvent.TrackPublished, syncCreatorAudioSubscriptions);
    room.on(RoomEvent.TrackUnpublished, syncCreatorAudioSubscriptions);
    return () => {
      room.off(RoomEvent.ParticipantConnected, syncCreatorAudioSubscriptions);
      room.off(RoomEvent.TrackPublished, syncCreatorAudioSubscriptions);
      room.off(RoomEvent.TrackUnpublished, syncCreatorAudioSubscriptions);
      room.remoteParticipants?.forEach?.((participant) => {
        const identity = String(participant.identity || "");
        if (!identity.startsWith("creator_") && !/^Camera-\d+$/.test(identity)) return;
        participant.trackPublications?.forEach?.((publication) => {
          const isAudio = publication.kind === Track.Kind.Audio
            || publication.source === Track.Source.Microphone
            || publication.source === Track.Source.ScreenShareAudio;
          if (isAudio && publication.isSubscribed) publication.setSubscribed?.(false);
        });
      });
    };
  }, [enabled, room]);

  return enabled ? <RoomAudioRenderer /> : null;
};

// Pro Mode Stage - Only shows the active camera selected by the control panel
const ProModeStage = ({ eventId, orientation = 'portrait', viewerDevice, audioEnabled = true }) => {
  const [activeDeviceId, setActiveDeviceId] = useState(null);
  const [activeParticipantIdentity, setActiveParticipantIdentity] = useState(null);
  const [programTransition, setProgramTransition] = useState(null);
  const room = useRoomContext();
  const activeParticipantIdentityRef = useRef(null);
  const transitionActiveRef = useRef(false);
  const transitionTimersRef = useRef([]);
  const transitionSequenceRef = useRef(0);
  
  const tracks = useTracks(
    [
      { source: Track.Source.Camera, withPlaceholder: true },
      { source: Track.Source.ScreenShare, withPlaceholder: false },
      { source: Track.Source.Microphone, withPlaceholder: false },
    ],
    { onlySubscribed: false }
  );

  useEffect(() => {
    const fetchActiveDevice = async () => {
      try {
        const response = await axiosInstance.get(`/pro-mode/session/${eventId}`);
        const session = response.data;
        if (session.active_device_id && !transitionActiveRef.current) {
          setActiveDeviceId(session.active_device_id);
          const deviceNumber = session.active_device_id.split('-device-')[1];
          if (deviceNumber) {
            setActiveParticipantIdentity(`Camera-${deviceNumber}`);
          }
        }
      } catch (error) {
        console.log('Pro Mode session not found, showing all tracks');
      }
    };

    fetchActiveDevice();
    const interval = setInterval(fetchActiveDevice, 5000);
    return () => clearInterval(interval);
  }, [eventId]);

  useEffect(() => {
    activeParticipantIdentityRef.current = activeParticipantIdentity;
  }, [activeParticipantIdentity]);

  useEffect(() => () => {
    transitionTimersRef.current.forEach((timer) => clearTimeout(timer));
    transitionTimersRef.current = [];
  }, []);

  // Program changes arrive over LiveKit's reliable data channel. CUT switches
  // immediately; the other modes retain both feeds and swap at their midpoint.
  // The slower API poll above remains as reconnect backup.
  useEffect(() => {
    if (!room) return undefined;
    const handleProgramCommand = (payload, participant, _kind, topic) => {
      if (topic !== PRO_MODE_COMMAND_TOPIC || participant?.identity !== 'control-panel') return;
      try {
        const command = JSON.parse(new TextDecoder().decode(payload));
        if (command.type !== 'device_switch' || !command.active_device_id) return;
        const deviceNumber = String(command.active_device_id).split('-device-')[1];
        if (!deviceNumber) return;
        const incomingIdentity = `Camera-${deviceNumber}`;
        const outgoingIdentity = activeParticipantIdentityRef.current;
        const transitionType = command.transition_type || command.transition || 'cut';
        const durationMs = outgoingIdentity && outgoingIdentity !== incomingIdentity
          ? getProModeTransitionDuration(transitionType)
          : 0;

        transitionTimersRef.current.forEach((timer) => clearTimeout(timer));
        transitionTimersRef.current = [];

        if (!outgoingIdentity || outgoingIdentity === incomingIdentity) {
          transitionActiveRef.current = false;
          setProgramTransition(null);
          setActiveDeviceId(command.active_device_id);
          setActiveParticipantIdentity(incomingIdentity);
          return;
        }

        transitionActiveRef.current = true;
        setProgramTransition({
          id: ++transitionSequenceRef.current,
          type: transitionType,
          outgoingIdentity,
          incomingIdentity,
          complete: durationMs === 0,
        });
        if (durationMs === 0) {
          setActiveDeviceId(command.active_device_id);
          setActiveParticipantIdentity(incomingIdentity);
          transitionActiveRef.current = false;
          return;
        }
        const midpointTimer = setTimeout(() => {
          setActiveDeviceId(command.active_device_id);
          setActiveParticipantIdentity(incomingIdentity);
        }, Math.round(durationMs / 2));
        const completionTimer = setTimeout(() => {
          transitionActiveRef.current = false;
          setProgramTransition((current) => (
            current?.incomingIdentity === incomingIdentity
              ? { ...current, complete: true }
              : current
          ));
        }, durationMs);
        transitionTimersRef.current = [midpointTimer, completionTimer];
      } catch (commandError) {
        console.warn('[ProViewer] ignored invalid program switch command:', commandError);
      }
    };
    room.on(RoomEvent.DataReceived, handleProgramCommand);
    return () => room.off(RoomEvent.DataReceived, handleProgramCommand);
  }, [room]);

  const remoteTracks = tracks.filter((track) => track.participant && !track.participant.isLocal);
  
  const cameraVideoTracks = remoteTracks.filter((t) => {
    const identity = t.participant?.identity;
    const isCamera = identity && identity.startsWith('Camera-');
    const isVideoSource = t.source === Track.Source.Camera || t.source === Track.Source.ScreenShare;
    return isCamera && isVideoSource;
  });
  const cameraTrackKey = cameraVideoTracks
    .map((trackRef) => `${trackRef.participant?.identity}:${trackRef.publication?.trackSid || ''}`)
    .sort()
    .join('|');

  useEffect(() => {
    const transitionWarmIdentities = new Set(
      programTransition && !programTransition.complete
        ? [programTransition.outgoingIdentity, programTransition.incomingIdentity]
        : []
    );
    cameraVideoTracks.forEach((trackRef) => {
      const publication = trackRef.publication;
      if (!publication || trackRef.source !== Track.Source.Camera) return;
      const plan = getProViewerSubscription({
        participantIdentity: trackRef.participant?.identity,
        activeIdentity: activeParticipantIdentity,
        transitionIdentities: Array.from(transitionWarmIdentities),
      });
      if (publication.isSubscribed !== plan.subscribed) {
        publication.setSubscribed?.(plan.subscribed);
      }
      if (plan.subscribed) {
        publication.setVideoQuality?.(plan.quality === "high" ? VideoQuality.HIGH : VideoQuality.LOW);
        publication.setVideoFPS?.(plan.fps);
      }
    });

    // Only the selected program microphone is audible. Inactive camera phones
    // remain connected to the creator switcher without consuming viewer audio
    // bandwidth or creating overlapping audio during a cut.
    room?.remoteParticipants?.forEach?.((participant) => {
      if (!/^Camera-\d+$/.test(participant.identity || "")) return;
      const microphone = participant.getTrackPublication(Track.Source.Microphone);
      if (!microphone) return;
      const shouldSubscribe = participant.identity === activeParticipantIdentity;
      if (microphone.isSubscribed !== shouldSubscribe) {
        microphone.setSubscribed?.(shouldSubscribe);
      }
    });
  }, [activeParticipantIdentity, cameraTrackKey, cameraVideoTracks, orientation, programTransition, room]);

  let videoTracks = cameraVideoTracks;

  const outgoingTransitionTrack = programTransition
    ? cameraVideoTracks.find((track) => track.participant?.identity === programTransition.outgoingIdentity)
    : null;
  const incomingTransitionTrack = programTransition
    ? cameraVideoTracks.find((track) => track.participant?.identity === programTransition.incomingIdentity)
    : null;
  const hasTransitionTracks = Boolean(outgoingTransitionTrack && incomingTransitionTrack);
  
  if (activeParticipantIdentity) {
    const activeTrack = videoTracks.find((t) => 
      t.participant?.identity === activeParticipantIdentity
    );
    if (activeTrack) {
      videoTracks = [activeTrack];
    }
  }

  const programTrack = videoTracks[0] || null;

  return (
    <div className="flex h-full w-full items-center justify-center overflow-hidden bg-black">
      <style>{proModeViewerTransitionStyles}</style>
      {audioEnabled && <RoomAudioRenderer />}
      
      {videoTracks.length > 0 ? (
        <div className="h-full w-full overflow-hidden bg-black" data-pro-video-orientation="auto">
          {hasTransitionTracks ? (
            <div key={programTransition.id} className={`relative h-full w-full viewer-transition-${programTransition.type}`}>
              <AdaptiveCreatorVideo
                trackRef={outgoingTransitionTrack}
                orientation={orientation}
                viewerDevice={viewerDevice}
                className="viewer-program-feed viewer-program-feed-outgoing z-10"
              />
              <AdaptiveCreatorVideo
                trackRef={incomingTransitionTrack}
                orientation={orientation}
                viewerDevice={viewerDevice}
                className="viewer-program-feed viewer-program-feed-incoming z-20"
              />
            </div>
          ) : (
            <AdaptiveCreatorVideo
              trackRef={programTrack}
              orientation={orientation}
              viewerDevice={viewerDevice}
              testId="pro-viewer-program-video"
            />
          )}
        </div>
      ) : (
        <div className="w-full h-full flex items-center justify-center bg-black">
          <div className="text-center">
            <Video className="w-12 h-12 text-gray-600 mx-auto mb-3" />
            <p className="text-gray-400">Waiting for creator to start streaming...</p>
            {activeParticipantIdentity && (
              <p className="text-gray-500 text-sm mt-2">Active camera: {activeParticipantIdentity}</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// The portrait creator program stays edge-to-edge as the viewer viewport
// changes. LiveKit attaches the <video> element late, so listeners are
// (re)attached on every recompute.
// The native portrait camera program fills the viewer surface. Capture does
// not force a fixed 9:16 crop before this presentation layer runs.
const ViewerVideoSubscriptionGuard = ({ enabled = true, onRecover }) => {
  const room = useRoomContext();
  const recoverRef = useRef(onRecover);

  useEffect(() => {
    recoverRef.current = onRecover;
  }, [onRecover]);

  useEffect(() => {
    if (!enabled || !room) return undefined;

    let missingSince = Date.now();
    let lastRoomCheckAt = 0;

    const ensureCreatorVideo = () => {
      let hasCreatorParticipant = false;
      let hasRemoteVideoPublication = false;
      let hasSubscribedVideoTrack = false;

      room.remoteParticipants.forEach((participant) => {
        const identity = String(participant.identity || "");
        if (identity.startsWith("creator_")) hasCreatorParticipant = true;

        participant.trackPublications?.forEach((publication) => {
          const isVideo = publication.source === Track.Source.Camera
            || publication.source === Track.Source.ScreenShare;
          if (!isVideo) return;

          hasRemoteVideoPublication = true;
          try {
            if (!publication.isSubscribed) publication.setSubscribed(true);
          } catch (subscriptionError) {
            console.warn('[LiveKit] Could not restore viewer video subscription:', subscriptionError);
          }

          if (publication.isSubscribed && publication.track && !publication.isMuted) {
            hasSubscribedVideoTrack = true;
          }
        });
      });

      const viewerVideos = Array.from(document.querySelectorAll(
        '[data-testid="adaptive-creator-video"] video, [data-testid="pro-viewer-program-video"], .viewer-program-feed video',
      ));
      viewerVideos.forEach((video) => {
        if (video.readyState >= 2 && video.paused) video.play().catch(() => {});
      });
      const hasDecodedVideoFrame = viewerVideos.some((video) => (
        video.readyState >= 2 && video.videoWidth > 0 && video.videoHeight > 0
      ));

      if (hasRemoteVideoPublication && hasSubscribedVideoTrack && hasDecodedVideoFrame) {
        missingSince = Date.now();
        return;
      }

      const now = Date.now();
      if (now - missingSince >= 6500 && now - lastRoomCheckAt >= 12000) {
        lastRoomCheckAt = now;
        // A missing first frame is a media-subscription problem, not proof that
        // the LiveKit room is broken. Keep audio and playback permission intact
        // while the status check moves us only if the authoritative room changed.
        recoverRef.current?.(
          shouldForceViewerRoomReconnect(
            hasCreatorParticipant || hasRemoteVideoPublication
              ? "stalled-decoder"
              : "missing-publication",
          ),
        );
      }
    };

    ensureCreatorVideo();
    const interval = window.setInterval(ensureCreatorVideo, 750);
    return () => {
      window.clearInterval(interval);
    };
  }, [enabled, room]);

  return null;
};

const AdaptiveCreatorVideo = ({
  trackRef,
  orientation,
  viewerDevice,
  onVideoUnavailable,
  className = "",
  testId = "adaptive-creator-video",
}) => {
  const wrapRef = useRef(null);
  const deviceProfile = useMemo(
    () => resolveViewerDeviceProfile(viewerDevice, orientation),
    [viewerDevice, orientation],
  );
  const [sourcePortrait, setSourcePortrait] = useState(
    () => (orientation || "portrait") !== "landscape",
  );
  const [viewportPortrait, setViewportPortrait] = useState(() => (
    typeof window === "undefined" || window.innerHeight >= window.innerWidth
  ));

  useEffect(() => {
    const wrap = wrapRef.current;
    if (!wrap) return undefined;
    let raf;
    let videoEl = null;
    let recoveryRequested = false;
    const firstFrameDeadline = Date.now() + 4500;

    const schedule = () => { cancelAnimationFrame(raf); raf = requestAnimationFrame(recompute); };

    const recompute = () => {
      // The <video> mounts asynchronously (and can be replaced on reconnect) —
      // re-query every pass and move our listeners onto the current element.
      const el = wrap.querySelector('video');
      const bounds = wrap.getBoundingClientRect();
      if (bounds.width > 0 && bounds.height > 0) {
        setViewportPortrait(bounds.height >= bounds.width);
      }
      if (el !== videoEl) {
        if (videoEl) {
          videoEl.removeEventListener('loadedmetadata', schedule);
          videoEl.removeEventListener('loadeddata', schedule);
        }
        videoEl = el;
        if (el) {
          el.addEventListener('loadedmetadata', schedule);
          el.addEventListener('loadeddata', schedule);
        }
      }
      if (el && el.videoWidth && el.videoHeight) {
        recoveryRequested = false;
        setSourcePortrait(el.videoHeight >= el.videoWidth);
      } else if (el && el.readyState >= 2 && el.paused) {
        el.play().catch(() => {});
      }

      if (
        Date.now() >= firstFrameDeadline
        && (!el || !el.videoWidth || el.readyState < 2)
        && !recoveryRequested
      ) {
        recoveryRequested = true;
        onVideoUnavailable?.();
      }
    };

    const poll = setInterval(recompute, 800); // safety net (late tracks, silent swaps)
    schedule();

    return () => {
      cancelAnimationFrame(raf);
      clearInterval(poll);
      if (videoEl) {
        videoEl.removeEventListener('loadedmetadata', schedule);
        videoEl.removeEventListener('loadeddata', schedule);
      }
    };
  }, [trackRef, onVideoUnavailable]);

  // Keep the decoded program edge-to-edge in every viewer orientation. This
  // presentation-only crop never reacquires or resizes the creator's camera.
  const objectFit = getCameraDisplayFit(
    viewportPortrait ? "portrait" : "landscape",
    sourcePortrait ? { width: 1, height: 2 } : { width: 2, height: 1 },
  );

  return (
    <div
      ref={wrapRef}
      className={`relative h-full w-full overflow-hidden bg-black ${className}`}
      data-testid={testId}
      data-video-orientation={sourcePortrait ? "portrait" : "landscape"}
      data-viewer-device={deviceProfile.id}
      data-device-resolution={`${deviceProfile.width}x${deviceProfile.height}`}
      data-fit={objectFit}
    >
      <VideoTrack
        trackRef={trackRef}
        aria-hidden
        className="absolute inset-0 h-full w-full scale-110 blur-2xl opacity-60"
        style={{ objectFit: CREATOR_CAMERA_BACKGROUND_FIT }}
      />
      <DirectRemoteVideo
        trackRef={trackRef}
        className="absolute inset-0 z-10 h-full w-full"
        style={{ objectFit, objectPosition: "center" }}
        testId="direct-creator-video"
      />
    </div>
  );
};

// Simple Stage component for regular events
// IMPORTANT: Only shows video from participants with "creator_" identity prefix
// Stage component - Shows ONLY the creator's video feed
const Stage = ({
  orientation,
  viewerDevice,
  audioEnabled = true,
  onVideoUnavailable,
}) => {
  const room = useLiveKitRoom();
  const participants = useParticipants();
  const tracks = useTracks(
    [
      { source: Track.Source.Camera, withPlaceholder: false },
      { source: Track.Source.ScreenShare, withPlaceholder: false },
    ],
    { onlySubscribed: true }
  );

  // Debug logging
  useEffect(() => {
    try {
      console.log('[Stage] Room state:', room?.state);
      console.log('[Stage] Total participants:', participants.length);
      console.log('[Stage] Total tracks:', tracks.length);
      
      participants.forEach(p => {
        console.log(`[Stage] Participant: identity=${p.identity}, isLocal=${p.isLocal}, trackCount=${p.trackPublications?.size}`);
        p.trackPublications?.forEach((pub) => {
          console.log(`  - Track: ${pub.kind}, source=${pub.source}, subscribed=${pub.isSubscribed}, enabled=${pub.isEnabled}`);
        });
      });
    } catch {}
  }, [room?.state, participants.length, tracks.length]);

  // Filter for video tracks from CREATOR ONLY (identity starts with "creator_")
  const creatorVideoTracks = tracks.filter((trackRef) => {
    const participant = trackRef.participant;
    if (!participant) return false;
    
    // Skip local participant (viewer)
    if (participant.isLocal) return false;
    
    const identity = participant.identity || '';
    
    // Only accept creator tracks
    if (!identity.startsWith('creator_')) {
      return false;
    }
    
    // Must be camera or screen share
    const isVideo = trackRef.source === Track.Source.Camera || trackRef.source === Track.Source.ScreenShare;
    return isVideo;
  });

  // Find creator participant
  const creatorParticipant = participants.find(p => 
    !p.isLocal && p.identity?.startsWith('creator_')
  );

  console.log('[Stage] Creator video tracks found:', creatorVideoTracks.length);
  console.log('[Stage] Creator participant:', creatorParticipant?.identity);

  return (
    <div className="w-full h-full bg-black">
      {audioEnabled && <RoomAudioRenderer />}
      {audioEnabled && <AudioEnabler />}
      
      {creatorVideoTracks.length > 0 ? (
        // Creator's video — auto-adjusts to landscape/portrait switches
        <AdaptiveCreatorVideo
          trackRef={creatorVideoTracks[0]}
          orientation={orientation}
          viewerDevice={viewerDevice}
          onVideoUnavailable={onVideoUnavailable}
        />
      ) : creatorParticipant ? (
        // Creator is connected but no video track yet
        <div className="w-full h-full flex items-center justify-center">
          <div className="text-center">
            <div className="w-12 h-12 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-3" />
            <p className="text-gray-400">Creator connected, loading video...</p>
          </div>
        </div>
      ) : (
        // No creator in room yet
        <div className="w-full h-full flex items-center justify-center">
          <div className="text-center">
            <Video className="w-12 h-12 text-gray-600 mx-auto mb-3" />
            <p className="text-gray-400">Waiting for creator to start streaming...</p>
          </div>
        </div>
      )}
    </div>
  );
};

// Share Modal
const ShareModal = ({ isOpen, onClose, eventTitle, eventUrl }) => {
  if (!isOpen) return null;

  const copyLink = () => {
    navigator.clipboard.writeText(eventUrl);
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50" onClick={onClose}>
      <div className="bg-gray-900 rounded-xl p-5 max-w-sm w-full mx-4" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-white text-lg font-bold">Share Stream</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-white">
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="space-y-2">
          <a href={`https://twitter.com/intent/tweet?text=${encodeURIComponent(`Watch ${eventTitle} live!`)}&url=${encodeURIComponent(eventUrl)}`} 
            target="_blank" rel="noopener noreferrer"
            className="flex items-center gap-3 w-full bg-gray-800 hover:bg-gray-700 text-white p-2.5 rounded-lg">
            <span className="w-7 h-7 bg-gray-700 rounded-full flex items-center justify-center">𝕏</span>
            <span>Twitter</span>
          </a>
          <a href={`https://wa.me/?text=${encodeURIComponent(`Watch ${eventTitle} live! ${eventUrl}`)}`}
            target="_blank" rel="noopener noreferrer"
            className="flex items-center gap-3 w-full bg-gray-800 hover:bg-gray-700 text-white p-2.5 rounded-lg">
            <span className="w-7 h-7 bg-gray-700 rounded-full flex items-center justify-center">📱</span>
            <span>WhatsApp</span>
          </a>
          <button onClick={copyLink} className="flex items-center gap-3 w-full bg-blue-600 hover:bg-blue-700 text-white p-2.5 rounded-lg">
            <span className="w-7 h-7 bg-blue-500 rounded-full flex items-center justify-center">🔗</span>
            <span>Copy Link</span>
          </button>
        </div>
      </div>
    </div>
  );
};

// Overlay with LIVE badge - positioned at top
const StreamOverlay = ({ streamTime, onShare, viewerCount, orientation }) => {
  const formatTime = (seconds) => {
    const hrs = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    return `${hrs.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <>
      <div className="absolute top-3 left-12 flex items-center gap-2 z-10">
        <div className="bg-red-600 px-2.5 py-1 rounded flex items-center gap-1.5">
          <div className="w-2 h-2 bg-white rounded-full animate-pulse"></div>
          <span className="text-white font-bold text-xs">LIVE</span>
        </div>
        <div className="bg-black/60 backdrop-blur-sm px-2 py-1 rounded flex items-center gap-1.5">
          <Users className="w-3 h-3 text-white" />
          <span className="text-white font-medium text-xs">{viewerCount}</span>
        </div>
        <div className="bg-black/60 backdrop-blur-sm px-2 py-1 rounded flex items-center gap-1.5" data-testid="orientation-badge" title={`Streaming in ${orientation === 'landscape' ? 'Landscape' : 'Portrait'} Mode`}>
          <span className={`inline-block border border-white ${orientation === 'landscape' ? 'w-4 h-2.5' : 'w-2.5 h-4'} rounded-[2px]`} />
          <span className="text-white font-medium text-xs capitalize">{orientation === 'landscape' ? 'Landscape' : 'Portrait'}</span>
        </div>
      </div>
      
      <div className="absolute top-3 right-3 flex items-center gap-2 z-10">
        <div className="bg-black/60 backdrop-blur-sm px-2 py-1 rounded">
          <span className="text-white font-mono text-xs">{formatTime(streamTime)}</span>
        </div>
        <button onClick={onShare} className="bg-black/60 backdrop-blur-sm hover:bg-black/80 px-2 py-1 rounded flex items-center gap-1">
          <Share2 className="w-3 h-3 text-white" />
          <span className="text-white text-xs">Share</span>
        </button>
      </div>
    </>
  );
};

// Floating Chat Message - Single message that fades in and out
const FloatingChatMessage = ({ message, onComplete }) => {
  const [opacity, setOpacity] = useState(0);
  const onCompleteRef = useRef(onComplete);
  onCompleteRef.current = onComplete;
  
  useEffect(() => {
    const fadeInTimer = setTimeout(() => setOpacity(1), 10);
    const fadeOutTimer = setTimeout(() => setOpacity(0), 1700);
    const removeTimer = setTimeout(() => onCompleteRef.current?.(), 2400);
    
    return () => {
      clearTimeout(fadeInTimer);
      clearTimeout(fadeOutTimer);
      clearTimeout(removeTimer);
    };
  }, []);

  return (
    <div 
      className="chat-message-float"
      style={{
        opacity,
        transition: `opacity ${opacity === 0 ? '700ms' : '200ms'} ease-out`,
      }}
    >
      <div className="inline-flex items-center gap-2 bg-black/50 backdrop-blur-xl rounded-2xl px-4 py-2 max-w-[90%]">
        <span 
          className="font-bold text-sm"
          style={{ color: message.color || '#60a5fa' }}
        >
          {message.username}
        </span>
        <span className="text-white text-sm font-medium">{message.message}</span>
      </div>
    </div>
  );
};

// Viewer Chat Message Queue - Shows sent messages briefly
const ViewerChatFeedback = ({ messages }) => {
  const [displayMessages, setDisplayMessages] = useState([]);

  useEffect(() => {
    if (messages.length > 0) {
      const latest = messages[messages.length - 1];
      // Only add if not already displayed
      if (!displayMessages.find(m => m.id === latest.id)) {
        setDisplayMessages(prev => [...prev, latest]);
      }
    }
  }, [messages]);

  const handleComplete = (id) => {
    setDisplayMessages(prev => prev.filter(m => m.id !== id));
  };

  return (
    <div className="absolute bottom-16 left-4 right-4 pointer-events-none z-20 flex flex-col items-start gap-2" style={{ maxHeight: '10%' }}>
      {displayMessages.slice(-3).map((msg) => (
        <FloatingChatMessage 
          key={msg.id} 
          message={msg} 
          onComplete={() => handleComplete(msg.id)}
        />
      ))}
    </div>
  );
};

// Floating Reaction - smooth continuous rise with gentle sway + fade
const FloatingReaction = ({ emoji, startX, onComplete }) => {
  const onCompleteRef = useRef(onComplete);
  onCompleteRef.current = onComplete;
  // Per-instance randomness so a burst doesn't move in lockstep.
  const cfg = useRef({
    sway: (Math.random() - 0.5) * 60,
    rise: 210 + Math.random() * 70,
    dur: 2.4 + Math.random() * 0.8,
    rot: (Math.random() - 0.5) * 24,
  }).current;

  useEffect(() => {
    const removeTimer = setTimeout(() => onCompleteRef.current?.(), cfg.dur * 1000);
    return () => clearTimeout(removeTimer);
  }, [cfg.dur]);

  return (
    <div
      className="absolute pointer-events-none"
      style={{
        left: `${startX}%`,
        bottom: '80px',
        fontSize: '2.5rem',
        textShadow: '0 2px 10px rgba(0,0,0,0.5)',
        zIndex: 25,
        willChange: 'transform, opacity',
        animation: `reactionFloat ${cfg.dur}s cubic-bezier(0.22, 0.61, 0.36, 1) forwards`,
        '--sway': `${cfg.sway}px`,
        '--rise': `${cfg.rise}px`,
        '--rot': `${cfg.rot}deg`,
      }}
    >
      {emoji}
    </div>
  );
};

// Reaction Animation Container
const ReactionAnimationContainer = ({ reactions }) => {
  const [activeReactions, setActiveReactions] = useState([]);

  useEffect(() => {
    if (reactions.length > 0) {
      const latest = reactions[reactions.length - 1];
      if (!activeReactions.find(r => r.id === latest.id)) {
        setActiveReactions(prev => [...prev, latest]);
      }
    }
  }, [reactions]);

  const handleComplete = (id) => {
    setActiveReactions(prev => prev.filter(r => r.id !== id));
  };

  return (
    <div className="absolute inset-0 pointer-events-none overflow-hidden z-20">
      {activeReactions.map((reaction) => (
        <FloatingReaction
          key={reaction.id}
          emoji={reaction.emoji}
          startX={reaction.startX}
          onComplete={() => handleComplete(reaction.id)}
        />
      ))}
    </div>
  );
};

// ShowMe Interaction Bar - Transparent overlay at bottom
const ShowMeInteractionBar = ({ 
  chatEnabled, 
  reactionsEnabled, 
  chatMessage, 
  setChatMessage, 
  onSendChat, 
  onSendReaction,
  onOpenTithe,
  chatConnected,
  reactionProgression,
  isReligion,
  reactionLabel
}) => {
  const { getCurrentEmoji, handleReactionTap, getTapCount } = reactionProgression;

  const handleReactionClick = (type) => {
    const emoji = handleReactionTap(type);
    onSendReaction(emoji, type);
  };

  // Reaction button with glow effect based on tap count
  const ReactionButton = ({ type, baseEmoji }) => {
    const currentEmoji = getCurrentEmoji(type);
    const tapCount = getTapCount(type);
    
    // Glow intensity increases with tap count
    const glowIntensity = Math.min(tapCount / 10, 1);
    const glowColor = {
      laugh: 'rgba(250, 204, 21, VAL)',
      heart: 'rgba(239, 68, 68, VAL)',
      fire: 'rgba(251, 146, 60, VAL)',
      clap: 'rgba(168, 85, 247, VAL)',
    }[type].replace('VAL', glowIntensity * 0.6);

    return (
      <button
        onClick={() => handleReactionClick(type)}
        className="w-12 h-12 flex items-center justify-center rounded-full transition-all duration-200 active:scale-90 hover:scale-110"
        style={{
          background: `rgba(255,255,255,0.1)`,
          boxShadow: tapCount > 0 ? `0 0 ${12 + tapCount * 2}px ${glowColor}` : 'none',
        }}
        data-testid={`reaction-btn-${type}`}
      >
        <span className="text-2xl">{currentEmoji || baseEmoji}</span>
      </button>
    );
  };

  return (
    <div className="absolute bottom-0 left-0 right-0 z-30 px-3 pt-3" style={{ paddingBottom: 'max(env(safe-area-inset-bottom, 0px), 12px)' }}>
      {/* Transparent glass bar */}
      <div className="flex items-center gap-2 mx-auto max-w-3xl bg-black/40 backdrop-blur-xl rounded-full px-4 py-2 border border-white/10">
        {/* Chat Icon */}
        {chatEnabled && (
          <div className="flex items-center gap-2 text-gray-400">
            <MessageCircle className="w-5 h-5" />
          </div>
        )}
        
        {/* Chat Input */}
        {chatEnabled && (
          <input
            type="text"
            value={chatMessage}
            onChange={(e) => setChatMessage(e.target.value)}
            onKeyPress={(e) => e.key === "Enter" && onSendChat()}
            placeholder={isReligion ? "Share with the congregation..." : "Send a message..."}
            className="flex-1 bg-transparent border-none outline-none text-white text-sm placeholder-gray-400 min-w-[120px]"
            data-testid="chat-input"
          />
        )}
        
        {/* Reaction Buttons */}
        {reactionsEnabled && (
          <div className="flex items-center gap-1" aria-label={reactionLabel}>
            <ReactionButton type="heart" baseEmoji={BASE_REACTION_EMOJIS.heart} />
            <ReactionButton type="laugh" baseEmoji={BASE_REACTION_EMOJIS.laugh} />
            <ReactionButton type="fire" baseEmoji={BASE_REACTION_EMOJIS.fire} />
            <ReactionButton type="clap" baseEmoji={BASE_REACTION_EMOJIS.clap} />
          </div>
        )}
        
        {/* Tithe Button */}
        <button
          onClick={onOpenTithe}
          title="Send a tithe"
          className="w-12 h-12 flex-shrink-0 flex items-center justify-center rounded-full bg-yellow-500/15 hover:bg-yellow-500/25 border border-yellow-400/30 transition-all duration-200 active:scale-90 hover:scale-110"
          data-testid="tithe-btn"
        >
          <Coins className="w-5 h-5 text-yellow-400" />
        </button>

        {/* Send Button */}
        {chatEnabled && (
          <button
            onClick={onSendChat}
            disabled={!chatMessage.trim()}
            className="w-10 h-10 flex items-center justify-center rounded-full bg-white/10 hover:bg-white/20 disabled:opacity-50 transition-colors"
            data-testid="send-chat-btn"
          >
            <Send className="w-4 h-4 text-white" />
          </button>
        )}
      </div>
    </div>
  );
};

// Settings Dropdown
const SettingsDropdown = ({ isOpen, onClose, videoQuality, setVideoQuality }) => {
  if (!isOpen) return null;

  return (
    <div className="absolute bottom-12 right-0 bg-black/90 backdrop-blur-xl border border-white/10 rounded-xl shadow-lg py-2 min-w-[140px] z-40">
      <div className="px-3 py-1 text-xs text-gray-400 uppercase tracking-wider">Quality</div>
      {['auto', '1080p', '720p', '480p'].map((q) => (
        <button
          key={q}
          onClick={() => { setVideoQuality(q); onClose(); }}
          className={`w-full px-3 py-2 text-left text-sm hover:bg-white/10 ${videoQuality === q ? 'text-blue-400' : 'text-white'}`}
        >
          {q === 'auto' ? 'Auto' : q}
        </button>
      ))}
    </div>
  );
};

const MAX_VIEWER_TITHE_EFFECT_HISTORY = 100;

const MINIMUM_CREDIT_TITHE = 0.01;
const TITHE_CHECKOUT_POLL_INTERVAL_MS = 2000;
const TITHE_CHECKOUT_MAX_POLLS = 150;

// The sender's PRIVATE tithe surface. The amount and the sender's own tithe
// history are visible only here (and mirrored on the creator dashboard).
const EmbeddedStripeCheckout = ({ clientSecret, publishableKey, onComplete, onError }) => {
  const mountRef = useRef(null);
  const completeRef = useRef(onComplete);
  const errorRef = useRef(onError);
  completeRef.current = onComplete;
  errorRef.current = onError;

  useEffect(() => {
    let active = true;
    let checkout;

    loadStripeJs()
      .then((Stripe) => Stripe(publishableKey).initEmbeddedCheckout({
        clientSecret,
        onComplete: () => completeRef.current?.(),
      }))
      .then((instance) => {
        if (!active) {
          instance.destroy();
          return;
        }
        checkout = instance;
        instance.mount(mountRef.current);
      })
      .catch((error) => {
        if (active) errorRef.current?.(error);
      });

    return () => {
      active = false;
      checkout?.destroy();
    };
  }, [clientSecret, publishableKey]);

  return <div ref={mountRef} className="min-h-[420px] w-full" data-testid="embedded-stripe-checkout" />;
};

const TitheModal = ({
  onClose,
  onSend,
  tithes,
  creatorName,
  tipLabel = "Tithe",
  checkout,
  checkoutStatus,
  onCheckoutComplete,
  onCheckoutError,
  wallet,
  onBuyCredits,
  creditPurchaseLoading,
}) => {
  const [presetId, setPresetId] = useState(TITHE_PRESETS[0].id);
  const [custom, setCustom] = useState("");
  const [showOtherCredits, setShowOtherCredits] = useState(false);
  const [otherCreditAmount, setOtherCreditAmount] = useState("");
  const [sendPending, setSendPending] = useState(false);
  const selectedPreset = getTithePresetById(presetId);
  const value = custom !== "" ? parseFloat(custom) : selectedPreset.amount;
  const paymentEmoji = custom === "" ? selectedPreset.emoji : undefined;
  const valid = !isNaN(value) && value >= MINIMUM_CREDIT_TITHE;

  return (
    <div
      className="absolute inset-0 z-[75] flex items-end justify-center bg-transparent"
      onClick={onClose}
      data-testid="tithe-modal"
    >
      <div
        className="max-h-[76%] w-full overflow-y-auto rounded-t-[1.65rem] border-t border-white/15 bg-[#101014]/95 px-5 pb-[max(env(safe-area-inset-bottom),1.5rem)] pt-3 shadow-2xl backdrop-blur-2xl animate-in slide-in-from-bottom duration-300 sm:max-w-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-3 flex justify-center"><span className="h-1.5 w-11 rounded-full bg-gray-700" /></div>
        <div className="flex items-center justify-between mb-1">
          <div className="flex items-center gap-2">
            <Coins className="w-5 h-5 text-yellow-400" />
            <h3 className="text-white text-lg font-bold">Send a {tipLabel}</h3>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-white" data-testid="tithe-close">
            <X className="w-5 h-5" />
          </button>
        </div>
        <p className="text-gray-400 text-xs mb-4">
          Support {creatorName || "the creator"}. The effect plays only for you and the creator.
        </p>

        {checkout ? (
          <div>
            <div className="mb-3 rounded-xl border border-blue-400/25 bg-blue-500/10 px-3 py-2 text-center">
              <p className="text-sm font-bold text-white">
                Add ${checkout.amount.toFixed(2)} in viewer credits
              </p>
              <p className="mt-0.5 text-[11px] text-blue-200">
                {checkoutStatus || "Enter payment securely without leaving the live stream."}
              </p>
              <p className="mt-1 text-[10px] text-gray-300">
                Your card is charged once. Spend these credits on smaller tips without another Stripe charge.
              </p>
            </div>
            <EmbeddedStripeCheckout
              clientSecret={checkout.clientSecret}
              publishableKey={checkout.publishableKey}
              onComplete={onCheckoutComplete}
              onError={onCheckoutError}
            />
          </div>
        ) : (
          <>
        <div className="mb-3 rounded-xl border border-emerald-400/25 bg-emerald-500/10 px-3 py-2.5">
          <div className="flex items-center justify-between gap-3">
            <span className="text-xs font-semibold text-emerald-100">Viewer credit balance</span>
            <span className="text-base font-extrabold text-white">${Number(wallet?.balance || 0).toFixed(2)}</span>
          </div>
          <div className="mt-2 grid grid-cols-4 gap-2">
            {(wallet?.packages || [
              { amount_cents: 500, amount: 5 },
              { amount_cents: 1000, amount: 10 },
              { amount_cents: 2000, amount: 20 },
              { amount_cents: 5000, amount: 50 },
            ]).map((item) => {
              const isOther = item.amount_cents === 5000;
              return (
                <button
                  key={item.amount_cents}
                  type="button"
                  onClick={() => {
                    if (isOther) {
                      setShowOtherCredits((current) => !current);
                      return;
                    }
                    setShowOtherCredits(false);
                    onBuyCredits(item.amount_cents);
                  }}
                  disabled={creditPurchaseLoading}
                  className="rounded-lg border border-emerald-300/20 bg-emerald-400/10 py-2 text-xs font-bold text-emerald-100 hover:bg-emerald-400/20 disabled:opacity-50"
                  data-testid={isOther ? "credit-package-other" : `credit-package-${item.amount_cents}`}
                >
                  {isOther ? "+$Other" : `+$${Number(item.amount).toFixed(0)}`}
                </button>
              );
            })}
          </div>
          {showOtherCredits && (
            <div className="mt-2 flex gap-2" data-testid="other-credit-amount-row">
              <div className="relative min-w-0 flex-1">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-xs text-emerald-100/70">$</span>
                <input
                  type="number"
                  min="5"
                  max="500"
                  step="1"
                  inputMode="decimal"
                  value={otherCreditAmount}
                  onChange={(event) => setOtherCreditAmount(event.target.value)}
                  placeholder="5–500"
                  className="h-10 w-full rounded-lg border border-emerald-300/20 bg-black/20 pl-7 pr-3 text-sm font-semibold text-white outline-none focus:border-emerald-300/60"
                  data-testid="other-credit-amount"
                />
              </div>
              <button
                type="button"
                onClick={() => onBuyCredits(Math.round(Number(otherCreditAmount) * 100))}
                disabled={creditPurchaseLoading || Number(otherCreditAmount) < 5 || Number(otherCreditAmount) > 500}
                className="h-10 rounded-lg bg-emerald-500 px-4 text-xs font-extrabold text-black disabled:opacity-40"
                data-testid="other-credit-submit"
              >
                Add credits
              </button>
            </div>
          )}
        </div>
        <div className="grid grid-cols-3 gap-2.5 mb-3 sm:grid-cols-5">
          {TITHE_PRESETS.map(({ id, amount, emoji }) => (
            <button
              key={id}
              onClick={() => {
                setPresetId(id);
                setCustom("");
              }}
              className={`flex min-h-16 flex-col items-center justify-center rounded-xl border py-3 text-sm font-bold transition-all ${
                custom === "" && presetId === id
                  ? "border-blue-400 bg-blue-500/25 text-white shadow-lg shadow-blue-950/30"
                  : "border-white/10 bg-white/5 text-white hover:bg-white/10"
              }`}
              data-testid={`tithe-preset-${id}`}
            >
              {emoji ? (
                <span className="mb-1 text-2xl leading-none">{emoji}</span>
              ) : (
                <Coins className={`mb-1 h-5 w-5 ${custom === "" && presetId === id ? "text-yellow-300" : "text-yellow-500"}`} />
              )}
              <span>${amount.toFixed(2)}</span>
            </button>
          ))}
          <div className="relative col-span-3 sm:col-span-5">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">$</span>
            <input
              type="number"
              min={MINIMUM_CREDIT_TITHE}
              step="0.01"
              value={custom}
              onChange={(e) => setCustom(e.target.value)}
              placeholder="Custom amount"
              className="w-full bg-white/5 border border-white/10 rounded-xl pl-7 pr-3 py-3 text-white text-sm outline-none focus:border-yellow-400"
              data-testid="tithe-custom"
            />
          </div>
        </div>
        <p className="mb-3 text-center text-[11px] font-semibold text-emerald-300">
          Tips use your prepaid balance, so small amounts do not create another card fee.
        </p>

        <button
          onClick={async () => {
            if (!valid || sendPending) return;
            setSendPending(true);
            try {
              await onSend(value, paymentEmoji);
            } finally {
              setSendPending(false);
            }
          }}
          disabled={!valid || sendPending}
          className="w-full rounded-full bg-gradient-to-b from-rose-500 to-rose-700 py-3.5 font-extrabold text-white shadow-lg shadow-rose-950/30 transition disabled:opacity-50"
          data-testid="tithe-send"
        >
          Send from credits — ${valid ? value.toFixed(2) : "0.00"}
        </button>

        <div className="flex items-center gap-1.5 justify-center mt-3 text-gray-500 text-[11px]">
          <Lock className="w-3 h-3" />
          Add credits once with Stripe, then tip from your balance.
        </div>
          </>
        )}

        {tithes.length > 0 && (
          <div className="mt-4 border-t border-white/10 pt-3">
            <p className="text-gray-400 text-xs font-semibold mb-2">Your {tipLabel.toLowerCase()}s (private)</p>
            <div className="space-y-1.5 max-h-28 overflow-y-auto">
              {tithes.slice(0, 5).map((t) => (
                <div key={t.id} className="flex items-center justify-between text-xs">
                  <span className="text-white font-semibold">${t.amount.toFixed(2)}</span>
                  <span className="text-gray-400">
                    {t.time.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                  </span>
                  <span className="text-green-400">{t.status}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const LiveStreamViewer = ({ eventId, userId, userName, userPicture, event, viewerDevice }) => {
  const navigate = useNavigate();
  const [token, setToken] = useState(null);
  const [wsUrl, setWsUrl] = useState(null);
  const [, setLoading] = useState(false);
  const [, setError] = useState(null);
  const [streamTime, setStreamTime] = useState(0);
  const [showShareModal, setShowShareModal] = useState(false);
  const [chatMessage, setChatMessage] = useState("");
  const [chatConnected, setChatConnected] = useState(false);
  const chatConnectedRef = useRef(false);
  const [viewerCount, setViewerCount] = useState(Number(event?.viewer_count) || 0);
  const [isProMode, setIsProMode] = useState(false);
  const [ivsPlaying, setIvsPlaying] = useState(false);
  const [ivsPlaybackState, setIvsPlaybackState] = useState(null);
  const [ivsFallbackRequired, setIvsFallbackRequired] = useState(false);
  const [realtimeProtectionEnabled, setRealtimeProtectionEnabled] = useState(null);
  const [roomRefreshRequest, setRoomRefreshRequest] = useState(0);
  const [roomInstanceKey, setRoomInstanceKey] = useState(0);
  const [audioRouteTransitionKey, setAudioRouteTransitionKey] = useState(0);
  const [speakerBridgePrepared, setSpeakerBridgePrepared] = useState(false);
  // Freeze the viewer stage as soon as this component mounts. Waiting until
  // credentials arrive would activate the hook during LiveKitRoom's first
  // connection and create a competing layout render on mobile WebKit.
  const liveViewport = useLiveViewportLock({
    active: true,
    allowRotation: true,
  });
  const chatWsRef = useRef(null);
  const joinedRoomNameRef = useRef("");
  const roomRefreshPendingRef = useRef(false);
  const tokenRequestSequenceRef = useRef(0);
  const liveKitRefreshTimerRef = useRef(null);
  const liveKitRecoveryForceRef = useRef(false);
  const lastForcedViewerRecoveryAtRef = useRef(0);
  const viewerCredentialScopeRef = useRef("");
  const ivsPlayingRef = useRef(false);
  // WebSocket delivery is the fast path. These IDs also let the durable
  // snapshot path recover activity from another backend instance without
  // replaying reactions the viewer already saw.
  const activitySeenIdsRef = useRef(new Set());
  const reactionSnapshotReadyRef = useRef(false);
  const chatRecoveryCursorRef = useRef(null);
  const chatRecoveryPromiseRef = useRef(null);
  const chatRecoveryEventRef = useRef(eventId);
  const chatRecoveryCompletedRef = useRef(false);
  const chatHasOpenedRef = useRef(false);
  const viewerAccessRevokedRef = useRef(false);
  chatConnectedRef.current = chatConnected;
  
  // Sent messages for viewer feedback
  const [sentMessages, setSentMessages] = useState([]);
  const [mutedUserIds, setMutedUserIds] = useState([]);
  // Sent reactions for animation
  const [sentReactions, setSentReactions] = useState([]);
  // Shared ShowMeLove/ShowMeClass reactions may arrive in a burst. The queue
  // spaces them out so every viewer can see each reaction without overlap.
  const remoteReactionQueueRef = useRef([]);
  const remoteReactionTimerRef = useRef(null);
  const drainRemoteReactionQueue = useCallback(() => {
    if (remoteReactionTimerRef.current !== null) return;

    const launchNext = () => {
      const nextReaction = remoteReactionQueueRef.current.shift();
      if (!nextReaction) {
        remoteReactionTimerRef.current = null;
        return;
      }

      setSentReactions((current) => [...current.slice(-39), nextReaction]);
      remoteReactionTimerRef.current = window.setTimeout(launchNext, 260);
    };

    launchNext();
  }, []);
  const enqueueRemoteReactions = useCallback((incoming) => {
    const reactions = Array.isArray(incoming) ? incoming : [incoming];
    remoteReactionQueueRef.current.push(...reactions.filter(Boolean));
    drainRemoteReactionQueue();
  }, [drainRemoteReactionQueue]);
  const handleIvsPlaybackChange = useCallback((active) => {
    const nextActive = Boolean(active);
    ivsPlayingRef.current = nextActive;
    setIvsPlaying(nextActive);
    if (nextActive) setIvsFallbackRequired(false);
  }, []);
  const handleIvsFallbackRequired = useCallback(() => {
    setIvsFallbackRequired(true);
  }, []);
  // Category-branded money effects: sender-only green dollars + private history.
  const [titheDollarEvents, setTitheDollarEvents] = useState([]);
  const animatedTitheIdsRef = useRef(new Set());
  const [myTithes, setMyTithes] = useState([]);
  const [showTithe, setShowTithe] = useState(false);
  const [titheCheckout, setTitheCheckout] = useState(null);
  const [titheCheckoutStatus, setTitheCheckoutStatus] = useState("");
  const [viewerWallet, setViewerWallet] = useState({ balance: 0, balance_cents: 0, packages: [] });
  const [creditPurchaseLoading, setCreditPurchaseLoading] = useState(false);
  const [showReport, setShowReport] = useState(false);
  const [reportReason, setReportReason] = useState("");
  const [reportSubmitting, setReportSubmitting] = useState(false);
  const [educationSpeaker, setEducationSpeaker] = useState({ status: "none", mic_enabled: false });
  const [educationSpeakerBusy, setEducationSpeakerBusy] = useState(false);
  const [audienceSpeakerLiveEnabled, setAudienceSpeakerLiveEnabled] = useState(
    event?.audience_speaker_enabled !== false,
  );
  const educationMicControlRef = useRef(null);
  const categoryKey = String(event?.category || "").trim().toLowerCase();
  const experience = getEventExperience(event?.category);
  const isShowMeExperienceEvent = isVisibleEventCategory(event?.category);
  const isEducationEvent = categoryKey === "education";
  const isPodcastEvent = categoryKey === "podcast";
  const isReligionEvent = categoryKey === "religion";
  // Audience microphone availability is a live control, not a video-source
  // selector. These categories stay on the same duplex LiveKit surface for
  // the entire session so turning the feature off cannot remount or resize
  // the viewer's video.
  const supportsAudienceSpeaker = isEducationEvent || isPodcastEvent || isReligionEvent;
  const isAudienceSpeakerEvent = (
    supportsAudienceSpeaker
    && audienceSpeakerLiveEnabled
  );
  const isEventCreator = Boolean(
    userId && event?.creator_id && String(userId) === String(event.creator_id),
  );
  const canRequestAudienceSpeaker = isAudienceSpeakerEvent && !isEventCreator;
  const speakerMode = isPodcastEvent ? "podcast" : isReligionEvent ? "religion" : "education";
  const speakerSelected = ["invited", "speaking"].includes(educationSpeaker.status);
  const previousSpeakerSelectedRef = useRef(false);
  const {
    needsLiveKitConnection,
    shouldRenderLiveKitStage,
    shouldAutoSubscribe,
    isSpeakerBridgeOnly,
  } = getViewerMediaHandoffPolicy({
    speakerSelected: speakerSelected && speakerBridgePrepared,
    ivsFallbackRequired,
    isProMode,
  });

  const markAudienceAudioRouteTransition = useCallback(() => {
    setAudioRouteTransitionKey((current) => current + 1);
  }, []);

  useLayoutEffect(() => {
    if (previousSpeakerSelectedRef.current === speakerSelected) return;
    previousSpeakerSelectedRef.current = speakerSelected;
    markAudienceAudioRouteTransition();
    setSpeakerBridgePrepared(false);
    if (!speakerSelected) return undefined;

    // Commit one paint with IVS already in picture-only mode before opening
    // the WebRTC audio session. This sequencing prevents iOS and Chromium
    // mobile browsers from resetting the visible media surface during join.
    const frame = window.requestAnimationFrame(() => setSpeakerBridgePrepared(true));
    return () => window.cancelAnimationFrame(frame);
  }, [markAudienceAudioRouteTransition, speakerSelected]);

  useEffect(() => {
    setAudienceSpeakerLiveEnabled(event?.audience_speaker_enabled !== false);
  }, [event?.audience_speaker_enabled]);

  const refreshViewerWallet = useCallback(async () => {
    try {
      const { data } = await axiosInstance.get("/wallet");
      setViewerWallet(data);
      return data;
    } catch (_error) {
      return null;
    }
  }, []);

  useEffect(() => {
    if (showTithe) refreshViewerWallet();
  }, [showTithe, refreshViewerWallet]);

  const submitStreamReport = useCallback(async () => {
    if (!reportReason || reportSubmitting) return;
    setReportSubmitting(true);
    try {
      const { data } = await axiosInstance.post(`/events/${eventId}/report`, {
        reason: reportReason,
        target_type: "livestream",
      });
      setShowReport(false);
      setShowShareModal(false);
      setReportReason("");
      viewerCredentialScopeRef.current = "";
      joinedRoomNameRef.current = "";
      chatWsRef.current?.close();
      setChatConnected(false);
      setToken(null);
      setWsUrl(null);
      if (data?.terminated) {
        toast.warning("This live stream was stopped after 10 matching viewer reports.");
      } else if (data?.refund?.status === "succeeded") {
        toast.success(`Report submitted. Your $${Number(data.refund.amount || 0).toFixed(2)} ticket refund was issued.`);
      } else if (data?.refund?.status === "pending") {
        toast.info("Report submitted. Your refund is being processed.");
      } else {
        toast.success("Report submitted. You have left this event.");
      }
      navigate("/my-tickets", { replace: true });
    } catch (error) {
      toast.error(error.response?.data?.detail || "Your report could not be submitted");
    } finally {
      setReportSubmitting(false);
    }
  }, [eventId, navigate, reportReason, reportSubmitting]);

  useEffect(() => {
    axiosInstance.get("/mutes")
      .then(({ data }) => setMutedUserIds(data.muted_user_ids || []))
      .catch(() => setMutedUserIds([]));
  }, []);

  const handleMuteUser = useCallback(async (message) => {
    const targetId = message?.user_id;
    if (!targetId) return;
    try {
      await axiosInstance.post(`/mutes/${targetId}`);
      setMutedUserIds((current) => [...new Set([...current, targetId])]);
      setSentMessages((current) => current.filter(
        (item) => String(item.user_id) !== String(targetId),
      ));
      toast.success(`${message.username || "User"} muted. Their chat will stay hidden for you.`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "This user could not be muted");
    }
  }, []);

  // The completed API response is the sender's only effect delivery path.
  const enqueueCoinEffect = useCallback(({
    id,
    dollar_effect_count: dollarEffectCount,
    amount,
    animation_started_at: animationStartedAt,
    leading_emojis: leadingEmojis,
    payment_emoji: paymentEmoji,
  }) => {
    const safeId = String(id || (Date.now() + Math.random()));
    if (animatedTitheIdsRef.current.has(safeId)) return;
    animatedTitheIdsRef.current.add(safeId);
    setTitheDollarEvents((prev) => {
      const fallbackSpec = getDonationEffectSpec(amount, paymentEmoji);
      const effect = {
        id: safeId,
        dollarCount: normalizeShowMeDonationDollarCount(
          dollarEffectCount || getShowMeDonationDollarCount(amount, paymentEmoji),
        ),
        leadingEmojis: Array.isArray(leadingEmojis)
          ? leadingEmojis
          : fallbackSpec.leadingEmojis,
        animationStartedAt,
      };
      const existingIndex = prev.findIndex((item) => String(item.id) === safeId);
      if (existingIndex === -1) {
        return [...prev, effect].slice(-MAX_VIEWER_TITHE_EFFECT_HISTORY);
      }
      const next = [...prev];
      next[existingIndex] = { ...next[existingIndex], ...effect };
      return next;
    });
  }, []);

  // Fetch one durable snapshot on entry and after each socket reconnect. The
  // WebSocket is authoritative while healthy; this cursor recovery only fills
  // activity missed during a disconnect instead of polling full snapshots.
  const recoverChatSnapshot = useCallback(() => {
    if (!eventId || (event?.chat_enabled === false && event?.reactions_enabled === false)) {
      return Promise.resolve(false);
    }
    if (chatRecoveryEventRef.current !== eventId) {
      chatRecoveryEventRef.current = eventId;
      chatRecoveryCursorRef.current = null;
      chatRecoveryPromiseRef.current = null;
      chatRecoveryCompletedRef.current = false;
    }
    if (chatRecoveryPromiseRef.current) return chatRecoveryPromiseRef.current;

    const recoveryEventId = eventId;
    const historyUrl = isEducationEvent
      ? `/events/${eventId}/questions`
      : `/events/${eventId}/chat`;
    const request = axiosInstance.get(historyUrl, {
      params: getChatRecoveryParams(chatRecoveryCursorRef.current),
    }).then((response) => {
      if (chatRecoveryEventRef.current !== recoveryEventId) return false;
      const payload = response.data || {};
      const messages = isEducationEvent
        ? (Array.isArray(payload.questions) ? payload.questions : [])
        : (Array.isArray(payload.messages) ? payload.messages : []);
      const history = messages.map((message) => ({
        id: message.id,
        user_id: message.user_id,
        username: message.user_name || message.username || "Viewer",
        user_picture: message.user_picture,
        message: message.message || "",
        color: "#60a5fa",
        timestamp: message.created_at || message.timestamp,
        sequence: message.sequence,
        pending: false,
        is_pinned: Boolean(message.is_pinned),
      }));
      if (history.length) {
        setSentMessages((current) => mergeChatMessages(current, history, 50));
      }

      if (
        areReactionsAudienceVisible(event?.category)
        && Array.isArray(payload.reactions)
      ) {
        const firstSnapshot = !reactionSnapshotReadyRef.current;
        const now = Date.now();
        const fresh = [];
        payload.reactions.forEach((reaction) => {
          const reactionId = reaction.id || `${reaction.reaction_type}:${reaction.created_at}`;
          const reactionKey = `reaction:${reactionId}`;
          if (activitySeenIdsRef.current.has(reactionKey)) return;
          activitySeenIdsRef.current.add(reactionKey);
          const createdAt = Date.parse(reaction.created_at || "");
          if (
            firstSnapshot
            && (!Number.isFinite(createdAt) || Math.abs(now - createdAt) > 5000)
          ) return;
          fresh.push({
            id: reactionId,
            emoji: reaction.emoji || BASE_REACTION_EMOJIS[reaction.reaction_type] || "\uD83D\uDC4D",
            startX: 20 + Math.random() * 60,
          });
        });
        reactionSnapshotReadyRef.current = true;
        if (fresh.length) enqueueRemoteReactions(fresh);
      }

      chatRecoveryCursorRef.current = advanceChatRecoveryCursor(
        chatRecoveryCursorRef.current,
        payload,
      );
      chatRecoveryCompletedRef.current = true;
      return true;
    }).catch((error) => {
      console.warn('[ShowMeLive chat] recovery unavailable:', error);
      return false;
    }).finally(() => {
      if (chatRecoveryPromiseRef.current === request) {
        chatRecoveryPromiseRef.current = null;
      }
    });
    chatRecoveryPromiseRef.current = request;
    return request;
  }, [
    enqueueRemoteReactions,
    event?.category,
    event?.chat_enabled,
    event?.reactions_enabled,
    eventId,
    isEducationEvent,
  ]);

  // Keep each branded experience on its category-specific emoji progression.
  const reactionProfile = getReactionProfileForCategory(event?.category);
  const reactionProgression = useReactionProgression(reactionProfile);

  // Playback now follows rotation automatically; never block viewers behind a
  // manual Portrait/Landscape prompt.
  const needsRotate = false;

  // Screen protection hook
  const {
    isProtected,
    showWarning,
    warningMessage,
    canContinue,
    violationCount,
    dismissWarning
  } = useScreenProtection(eventId, userId, realtimeProtectionEnabled);

  const revokeViewerAccess = useCallback((message) => {
    viewerAccessRevokedRef.current = true;
    viewerCredentialScopeRef.current = "";
    joinedRoomNameRef.current = "";
    const socket = chatWsRef.current;
    if (socket && socket.readyState < WebSocket.CLOSING) {
      socket.close(1000, "Event access revoked");
    }
    setChatConnected(false);
    setToken(null);
    setWsUrl(null);
    setError(message || "You cannot access this event.");
  }, []);

  // The chat socket carries immediate access-revocation messages. This slow,
  // jittered REST check remains as a safety net and becomes more frequent only
  // while the socket is unavailable.
  useEffect(() => {
    if (!eventId || !userId) return undefined;
    if (chatConnected) return undefined;
    viewerAccessRevokedRef.current = false;
    let active = true;
    let timer;
    let requestInFlight = false;
    const checkEventAccess = async () => {
      if (requestInFlight || viewerAccessRevokedRef.current) return;
      requestInFlight = true;
      try {
        const response = await axiosInstance.get(`/events/${eventId}/access-status`);
        if (!active || response.data?.can_access !== false) return;
        revokeViewerAccess(
          response.data?.message || "This creator has banned you from their events.",
        );
      } catch (accessError) {
        if (accessError.response?.status === 403 && active) {
          revokeViewerAccess(accessError.response?.data?.detail || "You cannot access this event.");
        }
      } finally {
        requestInFlight = false;
      }
    };

    const scheduleCheck = () => {
      if (!active || viewerAccessRevokedRef.current) return;
      timer = window.setTimeout(async () => {
        await checkEventAccess();
        scheduleCheck();
      }, getAccessStatusPollDelay(chatConnectedRef.current));
    };

    // Socket authentication and the viewer-token request already enforce
    // access on entry. Delay this redundant REST safety check so a large join
    // wave does not also create an immediate access-status request wave.
    scheduleCheck();
    return () => {
      active = false;
      window.clearTimeout(timer);
    };
  }, [chatConnected, eventId, revokeViewerAccess, userId]);

  // Stream timer
  useEffect(() => {
    const timer = setInterval(() => setStreamTime(prev => prev + 1), 1000);
    return () => clearInterval(timer);
  }, []);

  const requestViewerRoomRefresh = useCallback(() => {
    if (roomRefreshPendingRef.current) return;
    roomRefreshPendingRef.current = true;
    setRoomRefreshRequest((current) => current + 1);
  }, []);

  // Get a token for the room that is active right now. The request counter is
  // advanced when an upcoming event becomes live, a Pro session creates its
  // program room, or LiveKit reports a failed/disconnected first connection.
  useEffect(() => {
    let active = true;
    const requestSequence = ++tokenRequestSequenceRef.current;

    if (!needsLiveKitConnection) {
      viewerCredentialScopeRef.current = "";
      joinedRoomNameRef.current = "";
      setToken(null);
      setWsUrl(null);
      setLoading(false);
      setError(null);
      return () => {
        active = false;
      };
    }

    const getToken = async () => {
      console.log('[LiveKit] Getting token for event:', eventId, 'user:', userId);
      const credentialScope = `${eventId}:${userId}`;
      const isInitialRequest = viewerCredentialScopeRef.current !== credentialScope;
      try {
        if (isInitialRequest) {
          setLoading(true);
          setError(null);
        }
        const response = await axiosInstance.post("/livekit/join-as-viewer", {
          event_id: eventId,
          user_id: userId,
          user_name: userName || "Viewer",
          purpose: speakerSelected ? "speaker" : "fallback",
        });
        
        console.log('[LiveKit] Viewer room credentials received:', {
          roomName: response.data?.room_name,
          isProMode: Boolean(response.data?.is_pro_mode),
          streamReady: response.data?.stream_ready !== false,
        });
        
        if (!active || requestSequence !== tokenRequestSequenceRef.current) return;

        if (response.data.token && response.data.url) {
          viewerCredentialScopeRef.current = credentialScope;
          joinedRoomNameRef.current = response.data.room_name || "";
          setToken(response.data.token);
          setWsUrl(response.data.url);
          setIsProMode(response.data.is_pro_mode || false);
          if (response.data.education_speaker_status) {
            setEducationSpeaker(response.data.education_speaker_status);
          }
          setError(null);
          // A replacement token can be byte-for-byte identical when requested
          // within the same second. This explicit generation guarantees that a
          // failed first media session is actually remounted during recovery.
          setRoomInstanceKey((current) => current + 1);
          console.log('[LiveKit] Token set successfully');
        } else {
          throw new Error("Invalid response from server");
        }
      } catch (err) {
        if (!active || requestSequence !== tokenRequestSequenceRef.current) return;
        console.error('[LiveKit] Error getting token:', err);
        if (isInitialRequest) {
          setError(err.response?.data?.detail || err.message || "Failed to connect to stream");
        } else {
          console.warn('[LiveKit] Background room refresh failed; keeping the current connection.');
        }
      } finally {
        if (active && requestSequence === tokenRequestSequenceRef.current) {
          roomRefreshPendingRef.current = false;
          if (isInitialRequest) setLoading(false);
        }
      }
    };
    
    if (eventId && userId) {
      getToken();
    } else {
      console.error('[LiveKit] Missing eventId or userId:', { eventId, userId });
      setError("Missing event or user information");
      setLoading(false);
    }

    return () => {
      active = false;
    };
  }, [eventId, needsLiveKitConnection, roomRefreshRequest, speakerSelected, userId, userName]);

  const scheduleViewerRoomRecovery = useCallback((forceReconnect = false) => {
    if (forceReconnect) {
      // A broken audio-only viewer session should be remounted, but repeated
      // remounts while a creator intentionally has no camera would make the
      // viewer unstable. One forced recovery per 15 seconds is sufficient.
      if (Date.now() - lastForcedViewerRecoveryAtRef.current < 15000) return;
      liveKitRecoveryForceRef.current = true;
    }
    if (liveKitRefreshTimerRef.current !== null) return;
    liveKitRefreshTimerRef.current = window.setTimeout(async () => {
      liveKitRefreshTimerRef.current = null;
      const forceRequested = liveKitRecoveryForceRef.current;
      liveKitRecoveryForceRef.current = false;
      try {
        const response = await axiosInstance.get(`/livekit/stream-status/${eventId}`);
        const activeRoomName = String(response.data?.room_name || "");
        const roomChanged = Boolean(
          activeRoomName
          && joinedRoomNameRef.current
          && activeRoomName !== joinedRoomNameRef.current
        );
        if (response.data?.is_live && (forceRequested || roomChanged)) {
          if (forceRequested) lastForcedViewerRecoveryAtRef.current = Date.now();
          requestViewerRoomRefresh();
        }
      } catch (statusError) {
        console.warn('[LiveKit] Viewer recovery status check failed:', statusError);
      }
    }, 1200);
  }, [eventId, requestViewerRoomRefresh]);

  useEffect(() => () => {
    if (liveKitRefreshTimerRef.current !== null) {
      window.clearTimeout(liveKitRefreshTimerRef.current);
      liveKitRefreshTimerRef.current = null;
    }
    liveKitRecoveryForceRef.current = false;
  }, []);

  const reconnectAttemptsRef = useRef(0);
  const maxReconnectAttempts = 15;
  const messageQueueRef = useRef([]);
  const reactionBatchRef = useRef(new Map());
  const reactionBatchTimerRef = useRef(null);
  const isConnectingRef = useRef(false);
  const pingIntervalRef = useRef(null);

  // Process queued messages when connection is restored
  const processMessageQueue = useCallback(() => {
    if (chatWsRef.current?.readyState === WebSocket.OPEN && messageQueueRef.current.length > 0) {
      const queue = [...messageQueueRef.current];
      messageQueueRef.current = [];
      queue.forEach(msg => {
        try {
          chatWsRef.current.send(JSON.stringify(msg));
        } catch (e) {
          messageQueueRef.current.push(msg);
        }
      });
    }
  }, []);

  // Chat WebSocket - Robust connection with auto-reconnect
  const connectChatWebSocket = useCallback(() => {
    if (!eventId) return;
    if (isConnectingRef.current) return;
    
    // This lightweight socket is also the presence connection. Once IVS is
    // active, the viewer leaves LiveKit entirely instead of retaining a second
    // media-room connection just to keep the viewer count updated.
    
    let chatWsUrl = BACKEND_URL;
    if (!chatWsUrl) return;
    
    chatWsUrl = chatWsUrl.replace('https://', 'wss://').replace('http://', 'ws://');
    chatWsUrl = `${chatWsUrl}/api/ws/chat/${eventId}`;
    
    if (chatWsRef.current && (
      chatWsRef.current.readyState === WebSocket.OPEN
      || chatWsRef.current.readyState === WebSocket.CONNECTING
    )) return;
    
    isConnectingRef.current = true;
    
    try {
      const ws = new WebSocket(chatWsUrl);
      
      ws.onopen = () => {
        const isReconnect = chatHasOpenedRef.current;
        chatHasOpenedRef.current = true;
        isConnectingRef.current = false;
        setChatConnected(true);
        reconnectAttemptsRef.current = 0;
        processMessageQueue();
        if (isReconnect || !chatRecoveryCompletedRef.current) {
          recoverChatSnapshot();
        }
        
        if (pingIntervalRef.current) clearInterval(pingIntervalRef.current);
        pingIntervalRef.current = setInterval(() => {
          if (ws.readyState === WebSocket.OPEN) {
            ws.send("ping");
          }
        }, CHAT_SOCKET_HEARTBEAT_MS);
      };
      
      ws.onmessage = (wsEvent) => {
        if (wsEvent.data === "pong") return;
        // Render live chat + reactions broadcast from the server (from ALL
        // viewers and the creator). Previously this was a no-op, so nothing
        // other than the local user's own taps ever appeared.
        let data;
        try {
          data = JSON.parse(wsEvent.data);
        } catch {
          return;
        }

        if (data.type === "event_access_revoked") {
          revokeViewerAccess(data.reason || "You cannot access this event.");
        } else if (data.type === "connected") {
          if (typeof data.viewer_count === "number") setViewerCount(data.viewer_count);
          if (data.ivs_playback) setIvsPlaybackState(data.ivs_playback);
          if (typeof data.protection_enabled === "boolean") {
            setRealtimeProtectionEnabled(data.protection_enabled);
          }
          if (typeof data.audience_speaker_enabled === "boolean") {
            setAudienceSpeakerLiveEnabled(data.audience_speaker_enabled);
          }
        } else if (data.type === "stream_status" && data.ivs_playback) {
          setIvsPlaybackState(data.ivs_playback);
        } else if (data.type === "protection_settings" && typeof data.enabled === "boolean") {
          setRealtimeProtectionEnabled(data.enabled);
        } else if (data.type === "audience_speaker_availability" && typeof data.enabled === "boolean") {
          setAudienceSpeakerLiveEnabled(data.enabled);
          if (!data.enabled) {
            setEducationSpeaker({ status: "none", mic_enabled: false });
            educationMicControlRef.current?.setEnabled(false).catch(() => {});
          }
        } else if (data.type === "viewer_count" && typeof data.count === "number") {
          setViewerCount(data.count);
        } else if (data.type === "message") {
          chatRecoveryCursorRef.current = advanceChatRecoveryCursor(
            chatRecoveryCursorRef.current,
            { messages: [data] },
          );
          setSentMessages(prev => mergeChatMessages(prev, [{
              id: data.id || (Date.now() + Math.random()),
              user_id: data.user_id,
              username: data.username || "Viewer",
              user_picture: data.user_picture,
              message: data.message || "",
              color: data.color || "#60a5fa",
              timestamp: data.timestamp || data.created_at,
              sequence: data.sequence,
              pending: false,
              is_pinned: Boolean(data.is_pinned),
            }], 50));
        } else if (data.type === "chat_muted") {
          if (data.message_id) {
            setSentMessages((current) => current.filter(
              (message) => String(message.id) !== String(data.message_id),
            ));
          }
          toast.error(data.detail || "The event creator has muted your chat.");
        } else if ((data.type === "new_message" || data.type === "announcement") && data.message) {
          const incoming = data.message;
          chatRecoveryCursorRef.current = advanceChatRecoveryCursor(
            chatRecoveryCursorRef.current,
            { messages: [incoming] },
          );
          setSentMessages(prev => mergeChatMessages(prev, [{
              id: incoming.id || (Date.now() + Math.random()),
              user_id: incoming.user_id,
              username: incoming.user_name || incoming.username || "Viewer",
              user_picture: incoming.user_picture,
              message: incoming.message || "",
              color: "#60a5fa",
              timestamp: incoming.created_at || incoming.timestamp || data.timestamp,
              sequence: incoming.sequence,
              pending: false,
              is_pinned: Boolean(incoming.is_pinned),
            }], 50));
        } else if (data.type === "message_pinned" && data.message_id) {
          setSentMessages((current) => current.map((message) => (
            String(message.id) === String(data.message_id)
              ? { ...message, is_pinned: Boolean(data.is_pinned) }
              : data.is_pinned ? { ...message, is_pinned: false } : message
          )));
        } else if (data.type === "message_hidden" && data.message_id) {
          setSentMessages((current) => current.filter(
            (message) => String(message.id) !== String(data.message_id),
          ));
        } else if (data.type === "education_speaker_status" && data.speaker) {
          setEducationSpeaker(data.speaker);
          if (!["invited", "speaking"].includes(data.speaker.status)) {
            educationMicControlRef.current?.setEnabled(false).catch(() => {});
          }
          if (data.speaker.status === "invited") {
            toast.success("The creator invited you to speak. Tap the microphone to turn it on.");
          } else if (data.speaker.status === "muted") {
            toast.info("The creator turned your microphone off.");
          }
        } else if (data.type === "reaction") {
          chatRecoveryCursorRef.current = advanceChatRecoveryCursor(
            chatRecoveryCursorRef.current,
            { reactions: [data] },
          );
          const reactionId = data.id || (Date.now() + Math.random());
          const reactionKey = `reaction:${reactionId}`;
          if (activitySeenIdsRef.current.has(reactionKey)) return;
          activitySeenIdsRef.current.add(reactionKey);
          enqueueRemoteReactions(Array.from(
            { length: Math.min(Math.max(Number(data.count) || 1, 1), 5) },
            (_, index) => ({
              id: `${reactionId}:${index}`,
              emoji: data.emoji || "\uD83D\uDC4D",
              startX: 20 + Math.random() * 60,
            }),
          ));
        }
      };
      
      ws.onclose = (closeEvent) => {
        isConnectingRef.current = false;
        setChatConnected(false);
        
        if (pingIntervalRef.current) {
          clearInterval(pingIntervalRef.current);
          pingIntervalRef.current = null;
        }

        if (isAccessRevocationSocketClose(closeEvent.code)) {
          revokeViewerAccess(closeEvent.reason || "You cannot access this event.");
          return;
        }
        
        if (closeEvent.code !== 1000 && reconnectAttemptsRef.current < maxReconnectAttempts) {
          const delay = Math.min(500 * Math.pow(1.3, reconnectAttemptsRef.current), 5000);
          reconnectAttemptsRef.current += 1;
          setTimeout(connectChatWebSocket, delay);
        }
      };
      
      ws.onerror = () => {
        isConnectingRef.current = false;
      };
      
      chatWsRef.current = ws;
    } catch (err) {
      isConnectingRef.current = false;
    }
  }, [
    enqueueRemoteReactions,
    eventId,
    processMessageQueue,
    recoverChatSnapshot,
    revokeViewerAccess,
  ]);
  
  // Connect WebSocket when event loads
  useEffect(() => {
    if (eventId) {
      connectChatWebSocket();
    }
    
    return () => {
      if (pingIntervalRef.current) clearInterval(pingIntervalRef.current);
      if (chatWsRef.current) {
        chatWsRef.current.close(1000, 'Component unmounting');
      }
    };
  }, [eventId, connectChatWebSocket]);

  // Reset per-event dedupe and ordinary-reaction queue state.
  useEffect(() => {
    if (remoteReactionTimerRef.current !== null) {
      window.clearTimeout(remoteReactionTimerRef.current);
      remoteReactionTimerRef.current = null;
    }
    remoteReactionQueueRef.current = [];
    activitySeenIdsRef.current.clear();
    reactionSnapshotReadyRef.current = false;
    chatRecoveryCursorRef.current = null;
    chatRecoveryCompletedRef.current = false;
    chatHasOpenedRef.current = false;
    viewerAccessRevokedRef.current = false;
  }, [eventId]);

  useEffect(() => () => {
    if (remoteReactionTimerRef.current !== null) {
      window.clearTimeout(remoteReactionTimerRef.current);
    }
    remoteReactionQueueRef.current = [];
    window.clearTimeout(reactionBatchTimerRef.current);
    reactionBatchRef.current.clear();
  }, []);

  // WebSocket is the immediate invitation path; slow fallback polling only
  // applies to viewers actively waiting for microphone state changes.
  useEffect(() => {
    if (!canRequestAudienceSpeaker || !eventId) {
      setEducationSpeaker({ status: "none", mic_enabled: false });
      return undefined;
    }
    if (!["waiting", "invited", "speaking", "muted"].includes(educationSpeaker.status)) {
      return undefined;
    }
    if (chatConnected) return undefined;
    let cancelled = false;
    let timer;
    const syncSpeakerStatus = async () => {
      try {
        const { data } = await axiosInstance.get(`/events/${eventId}/speaker-hands/me`);
        if (!cancelled && data?.speaker) setEducationSpeaker(data.speaker);
      } catch (_error) {
        // A class ending or a brief network interruption should not disturb
        // healthy video playback.
      }
    };
    const scheduleSync = () => {
      timer = window.setTimeout(async () => {
        await syncSpeakerStatus();
        if (!cancelled) scheduleSync();
      }, getSpeakerStatusPollDelay(chatConnectedRef.current));
    };
    syncSpeakerStatus().finally(() => {
      if (!cancelled) scheduleSync();
    });
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [canRequestAudienceSpeaker, chatConnected, educationSpeaker.status, eventId]);

  const handleRaiseEducationHand = useCallback(async () => {
    if (educationSpeakerBusy || !canRequestAudienceSpeaker) return;
    const previousSpeaker = educationSpeaker;
    setEducationSpeakerBusy(true);
    setEducationSpeaker({ ...previousSpeaker, status: "waiting", mic_enabled: false });
    try {
      const { data } = await axiosInstance.post(`/events/${eventId}/speaker-hands`);
      setEducationSpeaker(data?.speaker || { status: "waiting", mic_enabled: false });
      toast.success(
        isPodcastEvent
          ? "Call-In requested. The creator can now invite you to speak."
          : isReligionEvent
            ? "Mic requested. The creator can now invite you to speak."
            : "Hand raised. The creator can now invite you to speak.",
      );
    } catch (error) {
      setEducationSpeaker(previousSpeaker);
      toast.error(error.response?.data?.detail || (
        isPodcastEvent
          ? "Your Call-In request could not be sent"
          : isReligionEvent
            ? "Your Mic request could not be sent"
            : "Your hand could not be raised"
      ));
    } finally {
      setEducationSpeakerBusy(false);
    }
  }, [canRequestAudienceSpeaker, educationSpeaker, educationSpeakerBusy, eventId, isPodcastEvent, isReligionEvent]);

  const handleCancelEducationHand = useCallback(async () => {
    if (educationSpeakerBusy) return;
    const previousSpeaker = educationSpeaker;
    setEducationSpeakerBusy(true);
    markAudienceAudioRouteTransition();
    setEducationSpeaker({ ...previousSpeaker, status: "cancelled", mic_enabled: false });
    try {
      // Leaving is authoritative and must not wait for a disconnected or
      // stalled LiveKit engine. The optimistic cancelled state unmounts the
      // temporary audio bridge; this call only accelerates local mic cleanup.
      educationMicControlRef.current?.setEnabled(false).catch(() => {});
      const { data } = await axiosInstance.delete(`/events/${eventId}/speaker-hands/me`);
      setEducationSpeaker(data?.speaker || { status: "cancelled", mic_enabled: false });
    } catch (error) {
      // Never restore the speaker seat after the viewer chose to leave. Keep
      // the bridge disconnected locally and retry durable server cleanup once
      // after a brief network interruption.
      window.setTimeout(() => {
        axiosInstance.delete(`/events/${eventId}/speaker-hands/me`).catch(() => {});
      }, 1500);
      toast.error(error.response?.data?.detail || "You left. Speaker cleanup will retry automatically.");
    } finally {
      setEducationSpeakerBusy(false);
    }
  }, [educationSpeaker, educationSpeakerBusy, eventId, markAudienceAudioRouteTransition]);

  const handleToggleEducationMic = useCallback(async () => {
    if (educationSpeakerBusy) return;
    if (educationSpeaker.status === "muted") {
      toast.info("The creator muted your microphone. Raise your hand again when invited.");
      return;
    }
    const nextEnabled = !educationSpeaker.mic_enabled;
    const previousSpeaker = educationSpeaker;
    setEducationSpeakerBusy(true);
    markAudienceAudioRouteTransition();
    setEducationSpeaker({
      ...previousSpeaker,
      status: nextEnabled ? "speaking" : "invited",
      mic_enabled: nextEnabled,
    });
    try {
      if (!educationMicControlRef.current) {
        throw new Error(
          isPodcastEvent
            ? "The live Podcast audio is still connecting"
            : isReligionEvent
              ? "The live service audio is still connecting"
              : "The live classroom audio is still connecting",
        );
      }
      await educationMicControlRef.current.setEnabled(nextEnabled);
      const { data } = await axiosInstance.post(
        `/events/${eventId}/speaker-hands/me/microphone`,
        { enabled: nextEnabled },
      );
      setEducationSpeaker(data?.speaker || {
        ...educationSpeaker,
        status: nextEnabled ? "speaking" : "invited",
        mic_enabled: nextEnabled,
      });
      toast.success(nextEnabled ? "Your microphone is on" : "Your microphone is off");
    } catch (error) {
      setEducationSpeaker(previousSpeaker);
      if (nextEnabled) {
        await educationMicControlRef.current?.setEnabled(false).catch(() => {});
      }
      toast.error(error.response?.data?.detail || error.message || "Microphone could not be changed");
    } finally {
      setEducationSpeakerBusy(false);
    }
  }, [educationSpeaker, educationSpeakerBusy, eventId, isPodcastEvent, isReligionEvent, markAudienceAudioRouteTransition]);

  // Send chat message with inline feedback (no toast)
  const handleSendChat = async () => {
    if (!chatMessage.trim()) return;
    
    const msgId = String(Date.now() + Math.random());
    const msg = { 
      type: "message", 
      id: msgId,
      user_id: userId,
      username: userName || "Viewer", 
      user_picture: userPicture,
      message: chatMessage.trim() 
    };

    if (isEducationEvent) {
      const questionText = msg.message;
      const optimisticMessage = {
        id: msg.id,
        user_id: userId,
        username: msg.username,
        user_picture: msg.user_picture,
        message: questionText,
        color: "#60a5fa",
        timestamp: new Date().toISOString(),
        pending: true,
      };
      setSentMessages((current) => mergeChatMessages(current, [optimisticMessage], 50));
      setChatMessage("");
      try {
        const { data } = await axiosInstance.post(`/events/${eventId}/chat`, {
          id: msg.id,
          message: questionText,
          message_type: "question",
        });
        const saved = data?.message || {};
        setSentMessages((current) => mergeChatMessages(current, [{
          ...optimisticMessage,
          id: saved.id || optimisticMessage.id,
          timestamp: saved.created_at || optimisticMessage.timestamp,
          pending: false,
        }], 50));
        toast.success("Private message sent to the creator.");
      } catch (error) {
        setSentMessages((current) => current.filter(
          (item) => String(item.id) !== String(optimisticMessage.id),
        ));
        setChatMessage(questionText);
        toast.error(error.response?.data?.detail || "Your private message could not be sent.");
      }
      return;
    }

    // Optimistic local echo + clear input immediately, regardless of socket state.
    setSentMessages(prev => mergeChatMessages(prev, [{
      id: msgId,
      user_id: userId,
      username: msg.username,
      user_picture: msg.user_picture,
      message: msg.message,
      color: '#60a5fa',
      timestamp: new Date().toISOString(),
      pending: true,
    }], 50));
    setChatMessage("");

    // Broadcast to everyone (creator + other viewers); queue if not connected yet.
    if (chatWsRef.current?.readyState === WebSocket.OPEN) {
      try {
        chatWsRef.current.send(JSON.stringify(msg));
      } catch (e) {
        messageQueueRef.current.push(msg);
      }
    } else {
      // Persist immediately when the socket is reconnecting. The creator's
      // snapshot feed will receive this message even when the viewer and
      // creator are connected to different backend instances.
      axiosInstance.post(`/events/${eventId}/chat`, {
        id: msg.id,
        message: msg.message,
        message_type: event?.chat_mode === "questions_only" ? "question" : "chat",
      }).catch(() => {
        messageQueueRef.current.push(msg);
        connectChatWebSocket();
      });
    }
  };

  // Send reaction with float animation (no toast)
  const handleSendReaction = (rawEmoji, type) => {
    // Guarantee a visible glyph even if the progression hook returns nothing,
    // so the reaction always animates for the sender AND a real emoji (never an
    // empty string) is broadcast to the creator and other viewers.
    const emoji = rawEmoji || BASE_REACTION_EMOJIS[type] || "\uD83D\uDC4D";
    const reactionId = String(Date.now() + Math.random());
    // Always float the reaction locally so the sender sees it immediately.
    const buttonPositions = { heart: 55, laugh: 65, fire: 75, clap: 85 };
    // Every event category broadcasts the same ID back to the audience. Mark
    // this sender-local echo as handled so it is not animated twice here.
    activitySeenIdsRef.current.add(`reaction:${reactionId}`);
    setSentReactions(prev => [...prev, {
      id: reactionId,
      emoji,
      startX: buttonPositions[type] || 70,
    }]);

    // Aggregate rapid taps per emoji into one control-plane frame. The sender
    // still sees every tap immediately, while a large audience no longer
    // creates one database write and one fanout per finger tap.
    const current = reactionBatchRef.current.get(type) || {
      id: reactionId,
      emoji,
      reaction_type: type,
      count: 0,
    };
    current.count = Math.min(current.count + 1, 50);
    reactionBatchRef.current.set(type, current);
    if (reactionBatchTimerRef.current !== null) return;
    reactionBatchTimerRef.current = window.setTimeout(() => {
      reactionBatchTimerRef.current = null;
      const batches = Array.from(reactionBatchRef.current.values());
      reactionBatchRef.current.clear();
      batches.forEach((batch) => {
        const msg = {
          type: "reaction_batch",
          ...batch,
          username: userName || "Viewer",
          user_id: userId,
        };
        activitySeenIdsRef.current.add(`reaction:${batch.id}`);
        if (chatWsRef.current?.readyState === WebSocket.OPEN) {
          try {
            chatWsRef.current.send(JSON.stringify(msg));
            return;
          } catch (_) {}
        }
        axiosInstance.post(`/events/${eventId}/reactions`, batch).catch(() => {
          messageQueueRef.current.push(msg);
        });
      });
    }, 300);
  };

  // Chat + reactions are core viewer features — always available in the player.
  // (Creator moderation still applies server-side; this just guarantees the UI
  // shows for every viewer even when an event omits the enabled flags.)
  const chatEnabled = event?.chat_enabled !== false;
  const reactionsEnabled = event?.reactions_enabled !== false;
  const showInteraction = true;

  // Tips spend prepaid credits immediately. Stripe is charged only when the
  // viewer buys a $5/$10/$20/$50 package.
  const handleSendTithe = async (amount, paymentEmoji) => {
    const id = String(Date.now() + Math.random());
    const donationEffect = getDonationEffectSpec(amount, paymentEmoji);
    setMyTithes((prev) => [{ id, amount, status: "Processing", time: new Date() }, ...prev]);
    try {
      const { data } = await axiosInstance.post(`/events/${eventId}/tithes`, {
        amount,
        tithe_id: id,
        anonymous: false,
        payment_emoji: paymentEmoji || null,
      });
      enqueueCoinEffect({
        id: data?.id || id,
        amount,
        dollar_effect_count: data?.dollar_effect_count || donationEffect.dollarCount,
        leading_emojis: data?.leading_emojis || donationEffect.leadingEmojis,
        payment_emoji: data?.payment_emoji || paymentEmoji,
        animation_started_at: data?.animation_started_at,
      });
      setViewerWallet((current) => ({
        ...current,
        balance: Number(data?.balance || 0),
        balance_cents: Number(data?.balance_cents || 0),
      }));
      setMyTithes((prev) => prev.map((item) => (
        item.id === id ? { ...item, status: "Completed" } : item
      )));
      toast.success(`Your $${amount.toFixed(2)} ${experience.tipSingular.toLowerCase()} was sent privately. Thank you 🙏`);
      return true;
    } catch (error) {
      setMyTithes((prev) => prev.map((item) => (
        item.id === id ? { ...item, status: "Failed" } : item
      )));
      toast.error(error?.response?.data?.detail || `Your ${experience.tipSingular.toLowerCase()} could not be sent.`);
      if (error?.response?.status === 402) refreshViewerWallet();
      return false;
    }
  };

  const handleBuyCredits = async (packageCents) => {
    if (creditPurchaseLoading) return;
    setCreditPurchaseLoading(true);
    setTitheCheckoutStatus("Opening secure Stripe checkout…");
    try {
      const { data } = await axiosInstance.post("/wallet/checkout", { package_cents: packageCents });
      setTitheCheckout({
        clientSecret: data.client_secret,
        publishableKey: data.publishable_key,
        sessionId: data.session_id,
        amount: packageCents / 100,
        type: "viewer_credits",
      });
      setTitheCheckoutStatus("Charge your card once to add viewer credits.");
      for (let attempt = 0; attempt < TITHE_CHECKOUT_MAX_POLLS; attempt += 1) {
        await new Promise((resolve) => window.setTimeout(resolve, TITHE_CHECKOUT_POLL_INTERVAL_MS));
        try {
          const status = await axiosInstance.get(`/wallet/checkout/${data.session_id}`);
          if (status.data?.payment_status === "paid") {
            setViewerWallet((current) => ({
              ...current,
              balance: Number(status.data?.balance || 0),
              balance_cents: Number(status.data?.balance_cents || 0),
            }));
            setTitheCheckout(null);
            setTitheCheckoutStatus("");
            toast.success(`$${(packageCents / 100).toFixed(2)} in viewer credits was added.`);
            break;
          }
          if (status.data?.status === "expired") break;
        } catch (_pollError) {
          // The signed webhook continues fulfillment through short network pauses.
        }
      }
    } catch (error) {
      setTitheCheckout(null);
      setTitheCheckoutStatus("");
      toast.error(error?.response?.data?.detail || "Viewer credits could not be purchased.");
    } finally {
      setCreditPurchaseLoading(false);
    }
  };

  const handleTitheCheckoutComplete = useCallback(() => {
    setTitheCheckoutStatus("Payment submitted. Confirming your viewer credits…");
  }, []);

  // Mobile viewer whose device orientation doesn't match the event: block the
  // player behind an orientation prompt until they match (or continue). No
  // LiveKitRoom is mounted here, so no playback.
  if (needsRotate) {
    return (
      <RotationPrompt
        isLandscape={isLandscape}
        orientation={event?.orientation || STREAM_ORIENTATION}
        onContinue={() => setRotateAck(true)}
        onBack={() => navigate(-1)}
      />
    );
  }

  return (
    <div
      className="live-viewport-lock overflow-hidden bg-black"
      style={{ height: `${liveViewport.height}px`, width: `${liveViewport.width}px` }}
      data-testid="live-stream-viewer"
      data-live-viewport-locked="true"
    >
      {/* CSS for ShowMe Interaction Viewer */}
      <style>{`
        .chat-message-float {
          animation: chatFadeIn 0.2s ease-out;
        }
        
        @keyframes chatFadeIn {
          from {
            opacity: 0;
            transform: translateY(10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        /* Tithe coins */
        @keyframes reactionFloat {
          0%   { transform: translate(0, 0) scale(0.6) rotate(0deg); opacity: 0; }
          12%  { transform: translate(calc(var(--sway) * 0.2), -24px) scale(1.15) rotate(calc(var(--rot) * 0.3)); opacity: 1; }
          30%  { transform: translate(calc(var(--sway) * 0.5), calc(var(--rise) * -0.32)) scale(1) rotate(calc(var(--rot) * 0.6)); opacity: 1; }
          70%  { transform: translate(calc(var(--sway) * 0.85), calc(var(--rise) * -0.72)) scale(1) rotate(var(--rot)); opacity: 0.9; }
          100% { transform: translate(var(--sway), calc(var(--rise) * -1)) scale(0.82) rotate(var(--rot)); opacity: 0; }
        }
        @keyframes showMeDonationsFadeOut {
          0%   { opacity: 0; }
          6%   { opacity: 1; }
          80%  { opacity: 1; }
          100% { opacity: 0; }
        }
        @keyframes titheCoinPop {
          0%   { transform: translate(-50%, 0) scale(0.4); opacity: 0; }
          15%  { opacity: 1; }
          45%  { transform: translate(calc(-50% + var(--tx)), calc(var(--ty) * -1)) scale(1); opacity: 1; }
          100% { transform: translate(calc(-50% + var(--tx) * 1.25), 60px) scale(0.9); opacity: 0; }
        }
        @keyframes titheCoinFall {
          0%   { transform: translateY(-48px) rotate(0deg); opacity: 0; }
          12%  { opacity: 1; }
          100% { transform: translateY(var(--fall)) rotate(var(--spin)); opacity: 1; }
        }
        @keyframes titheCoinFillUp {
          0%   { transform: translateY(60vh) rotate(0deg) scale(0.6); opacity: 0; }
          25%  { opacity: 1; }
          100% { transform: translateY(0) rotate(var(--spin)) scale(1); opacity: 1; }
        }
        @keyframes titheCoinWavePath {
          0%   { offset-distance: 0%; opacity: 0; }
          6%   { opacity: 1; }
          92%  { opacity: 1; }
          100% { offset-distance: 100%; opacity: 0; }
        }
        @keyframes titheCoinSpray {
          0%   { transform: translate(0, 0) rotate(0deg); opacity: 0; }
          15%  { opacity: 1; }
          100% { transform: translate(var(--dx), var(--drop)) rotate(-300deg); opacity: 0; }
        }
        @keyframes titheCoinRest {
          from { opacity: 1; }
          to   { opacity: 0; }
        }
      `}</style>
      
      {/* Screen Protection Overlay */}
      <ScreenProtectionOverlay
        showWarning={showWarning}
        warningMessage={warningMessage}
        canContinue={canContinue}
        violationCount={violationCount}
        onDismiss={dismissWarning}
        isProtected={false}
      />
      
      {/* Security Badge */}
      {violationCount > 0 && (
        <div className="absolute left-3 top-16 z-[55] flex items-center gap-1 rounded-full bg-yellow-500/85 px-2 py-1 text-xs text-black sm:left-4">
          <Shield className="w-3 h-3" />
          <span>{violationCount} warning{violationCount !== 1 ? 's' : ''}</span>
        </div>
      )}

      {/* Report modal */}
      {showReport && (
        <CreatorSettingsPortal>
        <div
          className="fixed inset-0 z-[220] flex items-center justify-center overflow-y-auto bg-black/70 p-4 backdrop-blur-sm"
          onClick={() => setShowReport(false)}
          data-testid="report-modal"
        >
          <div
            className="my-auto max-h-[calc(100dvh-2rem)] w-full max-w-sm overflow-y-auto rounded-2xl border border-white/10 bg-[#12151d] p-5 shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <Flag className="w-5 h-5 text-red-400" />
                <h3 className="text-white text-lg font-bold">Report this stream</h3>
              </div>
              <button onClick={() => setShowReport(false)} className="text-gray-400 hover:text-white">
                <X className="w-5 h-5" />
              </button>
            </div>
            <p className="text-gray-400 text-xs mb-3">Tell us what's wrong. Our team reviews every report.</p>
            <div className="space-y-2 mb-3">
              {LIVE_REPORT_REASONS.map((reason) => (
                <button
                  key={reason.value}
                  type="button"
                  onClick={() => setReportReason(reason.value)}
                  className={`w-full text-left text-sm px-3 py-2 rounded-lg border transition-colors ${
                    reportReason === reason.value
                      ? "bg-red-600/20 border-red-500/50 text-white"
                      : "bg-white/5 border-white/10 text-gray-200 hover:bg-white/10"
                  }`}
                  aria-pressed={reportReason === reason.value}
                  data-testid={`report-reason-${reason.value}`}
                >
                  {reason.label}
                </button>
              ))}
            </div>
            <button
              onClick={submitStreamReport}
              disabled={!reportReason || reportSubmitting}
              className="w-full py-3 rounded-xl bg-red-600 hover:bg-red-700 text-white font-bold disabled:opacity-50 transition-colors"
              data-testid="report-submit"
            >
              {reportSubmitting ? "Submitting…" : "Submit report"}
            </button>
          </div>
        </div>
        </CreatorSettingsPortal>
      )}

      {/* Full Screen Video - fits any screen in portrait or landscape */}
      <ProtectedContent 
        isProtected={isProtected} 
        showWarning={showWarning} 
        className="live-video-stage w-full h-full"
      >
        {needsLiveKitConnection && token && wsUrl && (
        <LiveKitRoom
          key={`viewer-room-${roomInstanceKey}-${token}`}
          serverUrl={wsUrl}
          token={token}
          connect
          audio={false}
          video={false}
          onConnected={() => {
            roomRefreshPendingRef.current = false;
          }}
          onDisconnected={() => {
            if (speakerSelected || !ivsPlayingRef.current) {
              scheduleViewerRoomRecovery(
                shouldForceViewerRoomReconnect("room-disconnected"),
              );
            }
          }}
          onError={(roomError) => {
            if (ivsPlayingRef.current && !speakerSelected) return;
            console.warn('[LiveKit] Viewer room error; checking active room:', roomError);
            scheduleViewerRoomRecovery(
              shouldForceViewerRoomReconnect("room-error"),
            );
          }}
          options={{
            // The Pro control panel keeps all camera inputs warm. Viewers use
            // manual program-only subscription in ProModeStage so phone
            // bandwidth and decoding power stay focused on what is on air.
            // A selected audience speaker publishes microphone audio only and
            // keeps watching IVS. Do not subscribe that bridge to a second
            // copy of the creator's audio/video program.
            autoSubscribe: shouldAutoSubscribe,
            adaptiveStream: shouldRenderLiveKitStage ? { pixelDensity: 1 } : false,
            dynacast: true,
            disconnectOnPageLeave: false,
            reconnectPolicy: {
              nextRetryDelayInMs: (ctx) => Math.min(1000 * Math.pow(2, ctx.retryCount), 10000),
              maxRetries: 20
            }
          }}
          data-lk-theme="default"
          data-speaker-bridge-only={isSpeakerBridgeOnly ? "true" : "false"}
          style={shouldRenderLiveKitStage
            ? { height: '100%', width: '100%' }
            : {
                position: 'absolute',
                width: '1px',
                height: '1px',
                overflow: 'hidden',
                opacity: 0,
                pointerEvents: 'none',
              }}
        >
          <ReligionViewerMicrophoneGuard enabled={isShowMeExperienceEvent && !isAudienceSpeakerEvent} />
          <EducationViewerMicrophoneController
            enabled={canRequestAudienceSpeaker}
            controlRef={educationMicControlRef}
            speakerStatus={educationSpeaker.status}
            microphoneEnabled={educationSpeaker.mic_enabled}
          />
          {isSpeakerBridgeOnly && (
            <AudienceSpeakerProgramAudio enabled={speakerSelected} />
          )}
          {shouldRenderLiveKitStage && (
            <>
              <ViewerVideoSubscriptionGuard
                enabled={!isProMode}
                onRecover={(forceReconnect) => scheduleViewerRoomRecovery(forceReconnect)}
              />
              {isProMode ? (
                <ProModeStage
                  eventId={eventId}
                  orientation={event?.orientation || STREAM_ORIENTATION}
                  viewerDevice={viewerDevice}
                  audioEnabled
                />
              ) : (
                <Stage
                  orientation={event?.orientation || STREAM_ORIENTATION}
                  viewerDevice={viewerDevice}
                  audioEnabled
                  onVideoUnavailable={() => scheduleViewerRoomRecovery(
                    shouldForceViewerRoomReconnect("missing-frame"),
                  )}
                />
              )}
              <StreamDiagnostics label={isProMode ? "Pro viewer" : "Basic viewer"} />
            </>
          )}
        </LiveKitRoom>
        )}
        <IvsPlaybackLayer
          eventId={eventId}
          orientation={event?.orientation || STREAM_ORIENTATION}
          onPlaybackChange={handleIvsPlaybackChange}
          onFallbackRequired={handleIvsFallbackRequired}
          playbackState={ivsPlaybackState}
          realtimeConnected={chatConnected}
          audioRouteTransitionKey={audioRouteTransitionKey}
          externalAudioMuted={speakerSelected}
        />
      </ProtectedContent>

      <LiveTopBar
        eventId={eventId}
        creatorName={event?.creator_name || event?.creator?.name || event?.creatorName}
        creatorPicture={event?.creator_picture || event?.creator?.picture}
        eventTitle={event?.title}
        viewerCount={viewerCount}
        streamTime={streamTime}
        isLive
        onReport={() => setShowReport(true)}
        onExit={() => navigate("/events")}
      />

      {chatEnabled && (
        <LiveChatLane
          messages={sentMessages}
          connected={chatConnected}
          audience="viewer"
          onMuteUser={handleMuteUser}
          mutedUserIds={mutedUserIds}
          isShowMeCongregation={isShowMeExperienceEvent}
          conversationLabel={experience.chatLabel}
        />
      )}

      {/* Reaction Animation Container */}
      {reactionsEnabled && (
        <LiveReactionLayer
          reactions={sentReactions}
          className="z-40"
          side={isShowMeExperienceEvent ? "right" : "spread"}
        />
      )}

      {/* Category-branded money effects: sender-only green dollars. */}
      <ShowMeDonationDollarLayer events={titheDollarEvents} className="z-40" />

      {/* Private tithe modal — sender-only surface for amount + history */}
      {showTithe && (
        <TitheModal
          onClose={() => {
            setShowTithe(false);
            setTitheCheckout(null);
            setTitheCheckoutStatus("");
          }}
          onSend={async (amount, paymentEmoji) => {
            const sent = await handleSendTithe(amount, paymentEmoji);
            if (sent) {
              setShowTithe(false);
              setTitheCheckout(null);
              setTitheCheckoutStatus("");
            }
            return sent;
          }}
          tithes={myTithes}
          creatorName={event?.creator_name || event?.creator?.name}
          tipLabel={experience.tipSingular}
          checkout={titheCheckout}
          checkoutStatus={titheCheckoutStatus}
          onCheckoutComplete={handleTitheCheckoutComplete}
          wallet={viewerWallet}
          onBuyCredits={handleBuyCredits}
          creditPurchaseLoading={creditPurchaseLoading}
          onCheckoutError={(checkoutError) => {
            console.error("[Stripe] Embedded Checkout failed", checkoutError);
            setTitheCheckout(null);
            setTitheCheckoutStatus("");
            toast.error("Stripe could not open inside the live stream. Please try again.");
          }}
        />
      )}

      {/* ShowMe Interaction Bar - Transparent overlay at bottom */}
      {showInteraction && (
        <ViewerInteractionDock
          chatEnabled={chatEnabled}
          reactionsEnabled={reactionsEnabled}
          chatMessage={chatMessage}
          setChatMessage={setChatMessage}
          onSendChat={handleSendChat}
          onSendReaction={handleSendReaction}
          onOpenTithe={() => setShowTithe(true)}
          onShare={() => setShowShareModal(true)}
          chatConnected={chatConnected}
          reactionProgression={reactionProgression}
          isReligion={isReligionEvent}
          reactionLabel={experience.reactionsLabel}
          chatPlaceholder={experience.chatPlaceholder}
          tipsEnabled={event?.tips_enabled !== false}
          tipLabel={experience.tipSingular}
          educationSpeaker={canRequestAudienceSpeaker ? educationSpeaker : null}
          speakerMode={speakerMode}
          onRaiseHand={handleRaiseEducationHand}
          onCancelHand={handleCancelEducationHand}
          onToggleSpeakerMic={handleToggleEducationMic}
          speakerBusy={educationSpeakerBusy}
        />
      )}

      <LiveShareSheet
        isOpen={showShareModal} 
        onClose={() => setShowShareModal(false)} 
        eventTitle={event?.title || "Live Stream"} 
        eventUrl={`${window.location.origin}/event/${eventId}`}
        onReport={() => {
          setShowShareModal(false);
          setShowReport(true);
        }}
      />
    </div>
  );
};

export default LiveStreamViewer;
